import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { collection, query, where, getDocs, doc, getDoc, addDoc, setDoc, updateDoc, deleteDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { useAuth } from "@/src/lib/AuthContext";
import { db as firestoreDb } from "@/src/lib/firebase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { 
  Plus, 
  Package, 
  ShoppingBag, 
  Users, 
  TrendingUp, 
  Settings,
  Store,
  MessageSquare,
  Loader2,
  Star,
  Edit,
  Trash2,
  Globe,
  Mail,
  Calendar,
  Clock,
  Video,
  HelpCircle,
  User,
  Zap,
  Check,
  MapPin,
  LayoutDashboard,
  RefreshCw,
  Send,
  UserCircle,
  UserPlus,
  Target,
  CheckCircle2,
  XCircle,
  Briefcase,
  Phone,
  MoreHorizontal,
  ChevronRight,
  ChevronLeft,
  X,
  Search,
  Download,
  CreditCard,
  FileText,
  Rocket,
  ArrowUpCircle,
  ShieldCheck,
  ArrowRight,
  Filter,
  Award,
  Link as LinkIcon,
  MousePointerClick,
  Share2,
  Gift,
  Workflow,
  Newspaper,
  GraduationCap,
  PlayCircle,
  Layers,
  ListChecks,
  ChevronDown,
  ChevronUp,
  Menu,
  Ticket,
  LogOut,
  Eye
} from "lucide-react";
import { Shop, Product, Order, Review, Meeting, ShopIntegration, Chat, Message, CRMClient, CRMStage, BusinessHours, BinanceSettings, PaymentSettings, InvoiceSettings, SalesFunnel, AffiliateStats, Certification, AutomationRule, BlogPost, Course, MallPromotion } from "@/src/types";
import { ORDER_STATUSES, CATEGORIES, COUNTRIES, PRICING_PLANS, MALL_ZONES } from "@/src/constants";
import { ProductCard } from "@/src/components/ProductCard";
import { EntrepreneurCalculator } from "@/src/components/EntrepreneurCalculator";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const DEFAULT_BUSINESS_HOURS: BusinessHours[] = [
  { day: 1, open: "09:00", close: "18:00", active: true },
  { day: 2, open: "09:00", close: "18:00", active: true },
  { day: 3, open: "09:00", close: "18:00", active: true },
  { day: 4, open: "09:00", close: "18:00", active: true },
  { day: 5, open: "09:00", close: "18:00", active: true },
  { day: 6, open: "09:00", close: "14:00", active: false },
  { day: 0, open: "09:00", close: "14:00", active: false },
];

const DAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

export function SellerDashboardPage() {
  const { user, profile, isAuthReady } = useAuth();
  const navigate = useNavigate();
  const [shops, setShops] = useState<Shop[]>([]);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [purchases, setPurchases] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Super Admin panel states
  const [allProfiles, setAllProfiles] = useState<any[]>([]);
  const [allShops, setAllShops] = useState<Shop[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [adminActiveSubTab, setAdminActiveSubTab] = useState<"users" | "shops" | "products">("users");
  const [adminSearchQuery, setAdminSearchQuery] = useState("");
  const [adminLoading, setAdminLoading] = useState(false);

  // Super Admin edit/add modal states
  const [isAdminEditUserOpen, setIsAdminEditUserOpen] = useState(false);
  const [isAdminAddUserOpen, setIsAdminAddUserOpen] = useState(false);
  const [selectedUserForAdmin, setSelectedUserForAdmin] = useState<any | null>(null);

  const [isAdminEditShopOpen, setIsAdminEditShopOpen] = useState(false);
  const [isAdminAddShopOpen, setIsAdminAddShopOpen] = useState(false);
  const [selectedShopForAdmin, setSelectedShopForAdmin] = useState<any | null>(null);

  const [isAdminEditProductOpen, setIsAdminEditProductOpen] = useState(false);
  const [isAdminAddProductOpen, setIsAdminAddProductOpen] = useState(false);
  const [selectedProductForAdmin, setSelectedProductForAdmin] = useState<any | null>(null);
  const [shopSelectionSearch, setShopSelectionSearch] = useState("");

  // Forms for admin
  const [adminUserForm, setAdminUserForm] = useState({
    uid: "",
    email: "",
    displayName: "",
    role: "buyer" as "buyer" | "seller" | "admin",
    phoneNumber: "",
    birthDate: "",
    city: "",
    country: ""
  });

  const [adminShopForm, setAdminShopForm] = useState({
    name: "",
    description: "",
    ownerUid: "",
    whatsapp: "",
    email: "",
    city: "",
    country: "Colombia",
    address: "",
    plan: "free" as "free" | "premium"
  });

  const [adminProductForm, setAdminProductForm] = useState({
    name: "",
    description: "",
    price: "",
    priceToAgree: false,
    shopId: "",
    category: CATEGORIES[0],
    type: "product" as "product" | "service",
    isClubExclusive: false,
    stock: "10"
  });

  const fetchAdminData = async () => {
    setAdminLoading(true);
    try {
      console.log("Loading all database tables for Admin view...");
      const usersSnap = await getDocs(collection(firestoreDb, "users"));
      const usersData = usersSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setAllProfiles(usersData);

      const shopsSnap = await getDocs(collection(firestoreDb, "shops"));
      const shopsData = shopsSnap.docs.map(doc => ({ ...doc.data(), id: doc.id })) as Shop[];
      setAllShops(shopsData);

      const productsSnap = await getDocs(collection(firestoreDb, "products"));
      const productsData = productsSnap.docs.map(doc => ({ ...doc.data(), id: doc.id })) as Product[];
      setAllProducts(productsData);
      
    } catch (err) {
      console.error("Error fetching admin data:", err);
      toast.error("Error al cargar datos administrativos");
    } finally {
      setAdminLoading(false);
    }
  };

  // --- USER HANDLERS ---
  const handleAdminSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const uRef = doc(firestoreDb, "users", adminUserForm.uid);
      await setDoc(uRef, {
        email: adminUserForm.email || "",
        displayName: adminUserForm.displayName || "",
        role: adminUserForm.role || "buyer",
        phoneNumber: adminUserForm.phoneNumber || "",
        birthDate: adminUserForm.birthDate || "",
        city: adminUserForm.city || "",
        country: adminUserForm.country || "",
        updatedAt: new Date().toISOString()
      }, { merge: true });
      toast.success("Usuario guardado con éxito");
      setIsAdminEditUserOpen(false);
      setIsAdminAddUserOpen(false);
      fetchAdminData();
    } catch (err: any) {
      console.error(err);
      toast.error("Error al guardar usuario: " + err.message);
    }
  };

  const handleAdminDeleteUser = async (uid: string) => {
    if (!confirm("¿Está seguro de eliminar este usuario? Esta acción es irreversible.")) return;
    try {
      await deleteDoc(doc(firestoreDb, "users", uid));
      toast.success("Usuario eliminado");
      fetchAdminData();
    } catch (err: any) {
      console.error(err);
      toast.error("Error al eliminar usuario: " + err.message);
    }
  };

  // --- SHOP HANDLERS ---
  const handleAdminSaveShop = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const shopPayload = {
        name: adminShopForm.name,
        description: adminShopForm.description || "",
        ownerUid: adminShopForm.ownerUid,
        whatsapp: adminShopForm.whatsapp || "",
        email: adminShopForm.email || "",
        location: {
          city: adminShopForm.city || "",
          country: adminShopForm.country || "Colombia",
          address: adminShopForm.address || ""
        },
        plan: adminShopForm.plan || "free",
        updatedAt: new Date().toISOString()
      };

      if (selectedShopForAdmin) {
        // Edit
        await updateDoc(doc(firestoreDb, "shops", selectedShopForAdmin.id), shopPayload);
        toast.success("Tienda actualizada");
      } else {
        // Add new
        await addDoc(collection(firestoreDb, "shops"), {
          ...shopPayload,
          createdAt: new Date().toISOString()
        });
        toast.success("Tienda creada con éxito");
      }
      setIsAdminEditShopOpen(false);
      setIsAdminAddShopOpen(false);
      fetchAdminData();
    } catch (err: any) {
      console.error(err);
      toast.error("Error al guardar tienda: " + err.message);
    }
  };

  const handleAdminDeleteShop = async (shopId: string) => {
    if (!confirm("¿Está seguro de eliminar esta tienda? Se borrará permanentemente.")) return;
    try {
      await deleteDoc(doc(firestoreDb, "shops", shopId));
      toast.success("Tienda eliminada");
      fetchAdminData();
    } catch (err: any) {
      console.error(err);
      toast.error("Error al eliminar tienda: " + err.message);
    }
  };

  // --- PRODUCT HANDLERS ---
  const handleAdminSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const productPayload = {
        name: adminProductForm.name,
        description: adminProductForm.description || "",
        price: Number(adminProductForm.price) || 0,
        priceToAgree: adminProductForm.priceToAgree,
        shopId: adminProductForm.shopId,
        category: adminProductForm.category || "General",
        type: adminProductForm.type || "product",
        isClubExclusive: adminProductForm.isClubExclusive || false,
        stock: Number(adminProductForm.stock) || 0,
        updatedAt: new Date().toISOString()
      };

      if (selectedProductForAdmin) {
        // Edit
        await updateDoc(doc(firestoreDb, "products", selectedProductForAdmin.id), productPayload);
        toast.success("Producto/Vitrina actualizada");
      } else {
        // Add new
        await addDoc(collection(firestoreDb, "products"), {
          ...productPayload,
          createdAt: new Date().toISOString()
        });
        toast.success("Producto/Vitrina creada con éxito");
      }
      setIsAdminEditProductOpen(false);
      setIsAdminAddProductOpen(false);
      fetchAdminData();
    } catch (err: any) {
      console.error(err);
      toast.error("Error al guardar producto: " + err.message);
    }
  };

  const handleAdminDeleteProduct = async (productId: string) => {
    if (!confirm("¿Está seguro de eliminar esta vitrina/producto?")) return;
    try {
      await deleteDoc(doc(firestoreDb, "products", productId));
      toast.success("Producto eliminado");
      fetchAdminData();
    } catch (err: any) {
      console.error(err);
      toast.error("Error al eliminar producto: " + err.message);
    }
  };

  // One-time migration to standardize "Estados Unidos" and "Madrid"
  useEffect(() => {
    const migrateData = async () => {
      if (!user || !shops.length) return;
      
      const usaVariants = [
        'usa', 'u.s.a.', 'u.s.a', 'us', 'u.s.', 'u.s', 
        'eeuu', 'ee.uu.', 'ee.uu', 'e.e.u.u.', 'e.e.u.u',
        'united states', 'united state', 'united states of america',
        'estados unidos', 'estados unido', 'estados unidos de america', 'estados unidos de américa'
      ];
      const madridVariants = ['madrid', 'MADRID', 'Madrid ', ' madrid'];
      
      try {
        const countryFixes = shops.filter(shop => {
          const lowerCountry = shop.location.country.trim().toLowerCase();
          return usaVariants.includes(lowerCountry);
        });

        const cityFixes = shops.filter(shop => 
          madridVariants.includes(shop.location.city.trim()) ||
          (shop.location.city.toLowerCase() === 'madrid' && shop.location.city !== 'Madrid')
        );

        if (countryFixes.length > 0 || cityFixes.length > 0) {
          console.log(`Standardizing ${countryFixes.length} countries and ${cityFixes.length} cities`);
          
          for (const shop of countryFixes) {
            await updateDoc(doc(firestoreDb, "shops", shop.id), {
              "location.country": "Estados Unidos",
              updatedAt: new Date().toISOString()
            });
          }

          for (const shop of cityFixes) {
            await updateDoc(doc(firestoreDb, "shops", shop.id), {
              "location.city": "Madrid",
              updatedAt: new Date().toISOString()
            });
          }

          fetchShops(); // Refresh list
        }
      } catch (error) {
        console.error("Error during location migration:", error);
      }
    };

    // Only run if the current user is the administrator (kensudev)
    if (user?.email === 'kensudev@gmail.com') {
      migrateData();
    }
  }, [user, shops]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isEditShopModalOpen, setIsEditShopModalOpen] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isReviewResponseModalOpen, setIsReviewResponseModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [reviewResponse, setReviewResponse] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [buyerMeetings, setBuyerMeetings] = useState<Meeting[]>([]);
  const [shopIntegration, setShopIntegration] = useState<ShopIntegration | null>(null);
  const [userProfileForm, setUserProfileForm] = useState({
    displayName: "",
    phoneNumber: "",
    city: "",
    country: ""
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [crmClients, setCrmClients] = useState<CRMClient[]>([]);
  const [crmSearchTerm, setCrmSearchTerm] = useState("");
  const [isLoadingCRM, setIsLoadingCRM] = useState(false);
  const [isUpdatingMeeting, setIsUpdatingMeeting] = useState(false);
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [funnels, setFunnels] = useState<SalesFunnel[]>([]);
  const [affiliateStats, setAffiliateStats] = useState<AffiliateStats>({
    clicks: 125,
    referrals: 12,
    totalEarnings: 350.50,
    pendingBalance: 45.20
  });
  const [certifications, setCertifications] = useState<Certification[]>([
    {
      id: "cert_1",
      name: "Experto en EmpreMarket",
      issuedAt: new Date().toISOString(),
      level: "basic",
      badgeUrl: "https://cdn-icons-png.flaticon.com/512/5110/5110292.png"
    }
  ]);
  const [automationRules, setAutomationRules] = useState<AutomationRule[]>([]);
  const [mallPromotions, setMallPromotions] = useState<MallPromotion[]>([]);
  const [isPromotionModalOpen, setIsPromotionModalOpen] = useState(false);
  const [promotionForm, setPromotionForm] = useState({
    title: "",
    description: "",
    discountCode: "",
    discountValue: "",
    expiryDate: "",
    image: "",
    type: "discount" as "discount" | "event" | "benefit"
  });
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([
    {
      id: "post_1",
      shopId: "shop_1",
      title: "Cómo escalar tu negocio este 2024",
      content: "Descubre las mejores estrategias...",
      published: true,
      createdAt: new Date().toISOString(),
      authorName: profile?.displayName || "Administrador"
    }
  ]);
  const [courses, setCourses] = useState<Course[]>([
    {
      id: "course_1",
      shopId: "shop_1",
      title: "Dominando el E-commerce 2024",
      description: "Aprende paso a paso cómo crear una tienda rentable desde cero.",
      instructorName: profile?.displayName || "Instructor",
      price: 49.99,
      modules: [
        {
          id: "mod_1",
          title: "Introducción al Negocio",
          lessons: [{ id: "les_1", title: "Bienvenida" }]
        }
      ],
      published: true,
      studentsCount: 150,
      createdAt: new Date().toISOString()
    }
  ]);
  
  // Pagination state
  const ITEMS_PER_PAGE = 5;
  const [productsPage, setProductsPage] = useState(1);
  const [ordersPage, setOrdersPage] = useState(1);
  const [purchasesPage, setPurchasesPage] = useState(1);
  const [meetingsPage, setMeetingsPage] = useState(1);
  const [buyerMeetingsPage, setBuyerMeetingsPage] = useState(1);
  const [chatsPage, setChatsPage] = useState(1);
  const [crmPage, setCrmPage] = useState(1);

  const [binanceForm, setBinanceForm] = useState<BinanceSettings>({
    merchantId: "",
    apiKey: "",
    secretKey: "",
    active: false
  });

  const [paymentSettingsForm, setPaymentSettingsForm] = useState<PaymentSettings>({
    zelle: { email: "", name: "", active: false },
    bizum: { phone: "", active: false },
    bank: { bankName: "", accountHolder: "", accountNumber: "", active: false },
    stripe: { active: false },
    paypal: { active: false, email: "" },
  });

  const [invoiceForm, setInvoiceForm] = useState<InvoiceSettings>({
    taxId: "",
    businessName: "",
    resolutionNumber: "",
    resolutionDate: "",
    prefix: "",
    fromNumber: 1,
    toNumber: 999999,
    active: false,
    footerText: ""
  });

  const [newClientForm, setNewClientForm] = useState({
    name: "",
    email: "",
    phone: "",
    notes: ""
  });

  // Form state
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    price: "",
    priceToAgree: false,
    category: CATEGORIES[0],
    type: "product" as "product" | "service",
    isClubExclusive: false,
    image: "",
    stock: "10",
    tags: ""
  });

  const [shopForm, setShopForm] = useState({
    name: "",
    description: "",
    whatsapp: "",
    website: "",
    email: "",
    instagram: "",
    facebook: "",
    logoURL: "",
    bannerURL: "",
    city: "",
    country: "Colombia",
    address: "",
    type: "both" as "products" | "services" | "both",
    categories: [] as string[],
    mallZone: "otros" as any,
    isClubAffiliated: false,
    isIntegral: false,
    businessHours: DEFAULT_BUSINESS_HOURS,
    customDomain: "",
    physicalLocation: {
      lat: 0,
      lng: 0,
      address: ""
    },
    premiumPhotos: ""
  });

  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedOrderForReview, setSelectedOrderForReview] = useState<Order | null>(null);
  const [newReview, setNewReview] = useState({
    rating: 5,
    comment: "",
    targetId: "" // shopId or productId
  });

  const handleConfirmReceipt = async (orderId: string) => {
    try {
      await setDoc(doc(firestoreDb, "orders", orderId), {
        status: "completed",
        updatedAt: new Date().toISOString()
      }, { merge: true });
      toast.success("¡Pedido confirmado como recibido!");
      fetchPurchases();
    } catch (error: any) {
      toast.error("Error al confirmar: " + error.message);
    }
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newReview.targetId) return;

    setIsSubmitting(true);
    try {
      const reviewData = {
        targetId: newReview.targetId,
        authorUid: user.uid,
        authorName: profile?.displayName || user.email?.split("@")[0] || "Usuario",
        rating: newReview.rating,
        comment: newReview.comment,
        createdAt: new Date().toISOString(),
        isVisible: false // Default to hidden until seller responds
      };

      await addDoc(collection(firestoreDb, "reviews"), reviewData);
      await updateTargetRating(newReview.targetId);
      toast.success("¡Gracias por tu reseña!");
      setIsReviewModalOpen(false);
      setNewReview({ rating: 5, comment: "", targetId: "" });
    } catch (error: any) {
      toast.error("Error al enviar reseña: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openReviewModal = (order: Order) => {
    setSelectedOrderForReview(order);
    // Default to the first product if available, otherwise the shop
    const defaultTarget = order.items.length > 0 ? order.items[0].productId : order.shopId;
    setNewReview({ ...newReview, targetId: defaultTarget, rating: 5, comment: "" });
    setIsReviewModalOpen(true);
  };

  const fetchShops = async () => {
    if (!user) return;
    try {
      console.log("Fetching shops. Role:", profile?.role);
      let shopsQuery;
      if (profile?.role === "admin") {
        shopsQuery = query(collection(firestoreDb, "shops"));
      } else {
        shopsQuery = query(collection(firestoreDb, "shops"), where("ownerUid", "==", user.uid));
      }
      const shopsSnap = await getDocs(shopsQuery);
      const shopsData = shopsSnap.docs.map(d => {
        const data = { ...(d.data() as any), id: d.id } as Shop;
        const testEmails = ['kensudev@gmail.com', 'kensudev@outlook.com', 'kensudev@yahoo.com'];
        if (user.email && (testEmails.includes(user.email) || user.email.startsWith('kensudev'))) {
          data.plan = 'premium';
        }
        return data;
      });
      console.log("Shops found:", shopsData.length);
      setShops(shopsData);
    } catch (error) {
      console.error("Error fetching shops:", error);
      toast.error("Error al cargar tus tiendas");
    }
  };

  const fetchProducts = async (shopId: string) => {
    try {
      const productsQuery = query(collection(firestoreDb, "products"), where("shopId", "==", shopId));
      const productsSnap = await getDocs(productsQuery);
      const productsData = productsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Product));
      setProducts(productsData);
      
      // Fetch reviews after products are loaded
      fetchReviews(shopId, productsData);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchSales = async (shopId: string) => {
    try {
      const ordersQuery = query(collection(firestoreDb, "orders"), where("shopId", "==", shopId));
      const ordersSnap = await getDocs(ordersQuery);
      const ordersData = ordersSnap.docs.map(d => ({ ...d.data(), id: d.id } as Order));
      setOrders(ordersData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (error) {
      console.error("Error fetching sales:", error);
    }
  };

  const fetchPurchases = async () => {
    if (!user) return;
    try {
      const purchasesQuery = query(collection(firestoreDb, "orders"), where("buyerUid", "==", user.uid));
      const purchasesSnap = await getDocs(purchasesQuery);
      const purchasesData = purchasesSnap.docs.map(d => ({ ...d.data(), id: d.id } as Order));
      setPurchases(purchasesData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (error) {
      console.error("Error fetching purchases:", error);
    }
  };

  const fetchMeetings = async (shopId: string) => {
    try {
      const meetingsQuery = query(collection(firestoreDb, "meetings"), where("shopId", "==", shopId));
      const meetingsSnap = await getDocs(meetingsQuery);
      const meetingsData = meetingsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Meeting));
      setMeetings(meetingsData.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()));
    } catch (error) {
      console.error("Error fetching meetings:", error);
    }
  };

  const fetchChats = async () => {
    if (!user) return;
    try {
      const chatsQuery = query(collection(firestoreDb, "chats"), where("participants", "array-contains", user.uid));
      const chatsSnap = await getDocs(chatsQuery);
      const chatsData = chatsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Chat));
      setChats(chatsData.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()));
    } catch (error) {
      console.error("Error fetching chats:", error);
    }
  };

  const fetchMessages = async (chatId: string) => {
    try {
      const messagesQuery = query(collection(firestoreDb, "chats", chatId, "messages"));
      const messagesSnap = await getDocs(messagesQuery);
      const messagesData = messagesSnap.docs.map(d => ({ ...d.data(), id: d.id } as Message));
      setMessages(messagesData.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()));
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const fetchCRM = async (shopId: string) => {
    setIsLoadingCRM(true);
    try {
      const response = await fetch(`/api/crm/${shopId}/clients`);
      if (response.ok) {
        const data = await response.json();
        setCrmClients(data);
      }
    } catch (error) {
      console.error("Error fetching CRM:", error);
    } finally {
      setIsLoadingCRM(false);
    }
  };

  const fetchPromotions = async (shopId: string) => {
    try {
      const q = query(collection(firestoreDb, "mall_promotions"), where("shopId", "==", shopId));
      const snap = await getDocs(q);
      setMallPromotions(snap.docs.map(doc => ({ ...doc.data(), id: doc.id } as MallPromotion)));
    } catch (error) {
      console.error("Error fetching promotions:", error);
    }
  };

  const handleCreatePromotion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShop) return;

    setIsSubmitting(true);
    try {
      const promoData = {
        shopId: selectedShop.id,
        shopName: selectedShop.name,
        ...promotionForm,
        createdAt: new Date().toISOString(),
        active: true,
        exclusiveForClub: true
      };

      await addDoc(collection(firestoreDb, "mall_promotions"), promoData);
      toast.success("Promoción creada para el Club");
      setIsPromotionModalOpen(false);
      setPromotionForm({
        title: "",
        description: "",
        discountCode: "",
        discountValue: "",
        expiryDate: "",
        image: "",
        type: "discount"
      });
      fetchPromotions(selectedShop.id);
    } catch (error: any) {
      toast.error("Error al crear promoción: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateMeetingStatus = async (meetingId: string, status: string) => {
    if (!selectedShop) return;
    setIsUpdatingMeeting(true);
    try {
      const meetingRef = doc(firestoreDb, "meetings", meetingId);
      
      const updateData: any = { 
        status, 
        updatedAt: new Date().toISOString() 
      };

      // If scheduled, ensure meetLink is set (internal)
      if (status === "scheduled") {
        updateData.meetLink = `/meeting/${meetingId}`;
      }

      await updateDoc(meetingRef, updateData);
      
      // Update CRM (Non-blocking)
      try {
        const meetingSnap = await getDoc(meetingRef);
        const meetingData = meetingSnap.data();
        if (meetingData) {
          const clientId = `${meetingData.shopId}_${meetingData.buyerUid}`;
          const clientRef = doc(firestoreDb, "crm_clients", clientId);
          await updateDoc(clientRef, {
            lastInteraction: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        }
      } catch (crmError) {
        console.error("Non-blocking CRM error:", crmError);
      }

      toast.success(status === "scheduled" ? "Cita aceptada" : "Cita rechazada");
    } catch (error) {
      console.error("Error updating meeting status:", error);
      toast.error("Error al actualizar la cita");
    } finally {
      setIsUpdatingMeeting(false);
    }
  };

  const handleUpdateCRMStage = async (clientId: string, stage: CRMStage) => {
    try {
      const response = await fetch(`/api/crm/clients/${clientId}/stage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage })
      });
      
      if (response.ok) {
        toast.success("Etapa actualizada");
        if (selectedShop) fetchCRM(selectedShop.id);
      }
    } catch (error) {
      toast.error("Error al actualizar etapa");
    }
  };

  const handleAddClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShop || !newClientForm.name || !newClientForm.email) return;

    try {
      const response = await fetch(`/api/crm/${selectedShop.id}/clients/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newClientForm)
      });
      
      if (response.ok) {
        toast.success("Cliente añadido");
        setIsAddingClient(false);
        setNewClientForm({ name: "", email: "", phone: "", notes: "" });
        fetchCRM(selectedShop.id);
      } else {
        const data = await response.json();
        toast.error(data.error || "Error al añadir cliente");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const exportCRM = (format: 'csv' | 'txt') => {
    if (crmClients.length === 0) return;
    
    let content = "";
    if (format === 'csv') {
      content = "Nombre,Email,Telefono,Etapa,Ultima Actividad,Notas\n";
      crmClients.forEach(client => {
        content += `"${client.name}","${client.email}","${client.phone || ''}","${client.stage}","${new Date(client.lastInteraction).toLocaleDateString()}","${client.notes || ''}"\n`;
      });
    } else {
      crmClients.forEach(client => {
        content += `Cliente: ${client.name}\nEmail: ${client.email}\nTel: ${client.phone || 'N/A'}\nEtapa: ${client.stage}\nActividad: ${new Date(client.lastInteraction).toLocaleDateString()}\nNotas: ${client.notes || 'N/A'}\n-------------------\n`;
      });
    }
    
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clientes_empremarket_${new Date().toISOString().split('T')[0]}.${format}`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Archivo ${format.toUpperCase()} exportado`);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedChat || !newMessage.trim()) return;

    setIsSendingMessage(true);
    try {
      const messageData = {
        chatId: selectedChat.id,
        senderUid: user.uid,
        text: newMessage.trim(),
        createdAt: new Date().toISOString()
      };

      await addDoc(collection(firestoreDb, "chats", selectedChat.id, "messages"), messageData);
      
      await setDoc(doc(firestoreDb, "chats", selectedChat.id), {
        lastMessage: newMessage.trim(),
        updatedAt: new Date().toISOString()
      }, { merge: true });

      setNewMessage("");
      fetchMessages(selectedChat.id);
      fetchChats();
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Error al enviar mensaje");
    } finally {
      setIsSendingMessage(false);
    }
  };

  const fetchShopIntegration = async (shopId: string) => {
    try {
      const integrationSnap = await getDoc(doc(firestoreDb, "shop_integrations", shopId));
      if (integrationSnap.exists()) {
        setShopIntegration(integrationSnap.data() as ShopIntegration);
      } else {
        setShopIntegration(null);
      }
    } catch (error) {
      console.error("Error fetching shop integration:", error);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSavingProfile(true);
    try {
      await setDoc(doc(firestoreDb, "users", user.uid), {
        displayName: userProfileForm.displayName,
        phoneNumber: userProfileForm.phoneNumber,
        location: {
          city: userProfileForm.city,
          country: userProfileForm.country
        },
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      toast.success("Perfil actualizado correctamente");
    } catch (error: any) {
      toast.error("Error al actualizar perfil: " + error.message);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleUpgrade = async (type: "monthly" | "annual") => {
    if (!selectedShop) return;
    setIsUpgrading(true);
    try {
      const expiryDate = new Date();
      if (type === "monthly") {
        expiryDate.setMonth(expiryDate.getMonth() + 1);
      } else {
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      }

      await setDoc(doc(firestoreDb, "shops", selectedShop.id), {
        plan: "premium",
        subscriptionStatus: "active",
        subscriptionExpiry: expiryDate.toISOString(),
        updatedAt: new Date().toISOString()
      }, { merge: true });

      toast.success(`¡Felicidades! Tu tienda ahora es Premium (${type === "monthly" ? "Mensual" : "Anual"})`);
      fetchShops();
      setIsSubscriptionModalOpen(false);
    } catch (error: any) {
      toast.error("Error al procesar la suscripción: " + error.message);
    } finally {
      setIsUpgrading(false);
    }
  };

  const updateTargetRating = async (targetId: string) => {
    try {
      // Fetch all reviews for this target to avoid index issues with composite queries
      const reviewsQuery = query(
        collection(firestoreDb, "reviews"),
        where("targetId", "==", targetId)
      );
      const reviewsSnap = await getDocs(reviewsQuery);
      const allReviewsData = reviewsSnap.docs.map(d => d.data() as Review);
      
      // Filter visible reviews in memory
      const visibleReviews = allReviewsData.filter(r => r.isVisible);
      
      const count = visibleReviews.length;
      const avgRating = count > 0 
        ? visibleReviews.reduce((acc, r) => acc + r.rating, 0) / count 
        : 0;

      // Try updating product first
      const productRef = doc(firestoreDb, "products", targetId);
      const productSnap = await getDoc(productRef);
      
      if (productSnap.exists()) {
        const productData = productSnap.data() as Product;
        await setDoc(productRef, {
          rating: avgRating,
          reviewCount: count
        }, { merge: true });

        // Also update the shop's aggregate rating
        if (productData.shopId) {
          await updateShopAggregateRating(productData.shopId);
        }
      } else {
        // Try updating shop directly
        const shopRef = doc(firestoreDb, "shops", targetId);
        const shopSnap = await getDoc(shopRef);
        if (shopSnap.exists()) {
          await updateShopAggregateRating(targetId);
        }
      }
    } catch (error) {
      console.error("Error updating target rating:", error);
    }
  };

  const updateShopAggregateRating = async (shopId: string) => {
    try {
      // A shop's rating should be the average of ALL visible reviews 
      // (both for the shop itself and all its products)
      
      // 1. Get shop reviews
      const shopReviewsQuery = query(
        collection(firestoreDb, "reviews"),
        where("targetId", "==", shopId)
      );
      const shopReviewsSnap = await getDocs(shopReviewsQuery);
      const shopReviews = shopReviewsSnap.docs.map(d => d.data() as Review).filter(r => r.isVisible);

      // 2. Get all products of this shop
      const productsQuery = query(
        collection(firestoreDb, "products"),
        where("shopId", "==", shopId)
      );
      const productsSnap = await getDocs(productsQuery);
      const productIds = productsSnap.docs.map(d => d.id);

      // 3. Get all product reviews (in chunks of 10 for Firestore 'in' limit)
      let productReviews: Review[] = [];
      for (let i = 0; i < productIds.length; i += 10) {
        const chunk = productIds.slice(i, i + 10);
        const pReviewsQuery = query(
          collection(firestoreDb, "reviews"),
          where("targetId", "in", chunk)
        );
        const pReviewsSnap = await getDocs(pReviewsQuery);
        productReviews = [...productReviews, ...pReviewsSnap.docs.map(d => d.data() as Review).filter(r => r.isVisible)];
      }

      const allReviews = [...shopReviews, ...productReviews];
      const totalCount = allReviews.length;
      const avgRating = totalCount > 0 
        ? allReviews.reduce((acc, r) => acc + r.rating, 0) / totalCount 
        : 0;

      await setDoc(doc(firestoreDb, "shops", shopId), {
        rating: avgRating,
        reviewCount: totalCount
      }, { merge: true });
    } catch (error) {
      console.error("Error updating shop aggregate rating:", error);
    }
  };

  const fetchReviews = async (shopId: string, currentProducts: Product[]) => {
    try {
      const shopReviewsQuery = query(collection(firestoreDb, "reviews"), where("targetId", "==", shopId));
      const shopReviewsSnap = await getDocs(shopReviewsQuery);
      let allReviews = shopReviewsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Review));
      
      if (currentProducts.length > 0) {
        // Fetch reviews for products (limit to first 10 for 'in' query)
        const productIds = currentProducts.map(p => p.id).slice(0, 10);
        const productReviewsQuery = query(collection(firestoreDb, "reviews"), where("targetId", "in", productIds));
        const productReviewsSnap = await getDocs(productReviewsQuery);
        allReviews = [...allReviews, ...productReviewsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Review))];
      }
      
      setReviews(allReviews.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (error) {
      console.error("Error fetching reviews:", error);
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar esta reseña?")) return;
    try {
      const reviewRef = doc(firestoreDb, "reviews", reviewId);
      const reviewSnap = await getDoc(reviewRef);
      const targetId = reviewSnap.exists() ? reviewSnap.data().targetId : null;

      await deleteDoc(reviewRef);
      if (targetId) await updateTargetRating(targetId);
      
      toast.success("Reseña eliminada");
      if (selectedShop) fetchReviews(selectedShop.id, products);
    } catch (error: any) {
      toast.error("Error al eliminar reseña: " + error.message);
    }
  };

  const toggleReviewVisibility = async (reviewId: string, currentVisibility: boolean) => {
    try {
      const reviewRef = doc(firestoreDb, "reviews", reviewId);
      await setDoc(reviewRef, {
        isVisible: !currentVisibility
      }, { merge: true });
      
      const reviewSnap = await getDoc(reviewRef);
      const targetId = reviewSnap.exists() ? reviewSnap.data().targetId : null;
      if (targetId) await updateTargetRating(targetId);

      toast.success(!currentVisibility ? "Reseña ahora es visible" : "Reseña ocultada");
      if (selectedShop) fetchReviews(selectedShop.id, products);
    } catch (error: any) {
      toast.error("Error al cambiar visibilidad: " + error.message);
    }
  };

  const handleRespondReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReview || !selectedShop) return;

    setIsSubmitting(true);
    try {
      await setDoc(doc(firestoreDb, "reviews", selectedReview.id), {
        sellerResponse: reviewResponse,
        sellerResponseAt: new Date().toISOString(),
        isVisible: true // Automatically make visible when responding
      }, { merge: true });
      
      await updateTargetRating(selectedReview.targetId);
      
      toast.success("Respuesta enviada y reseña hecha visible");
      setIsReviewResponseModalOpen(false);
      setReviewResponse("");
      fetchReviews(selectedShop.id, products);
    } catch (error: any) {
      toast.error("Error al responder: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openResponseModal = (review: Review) => {
    setSelectedReview(review);
    setReviewResponse(review.sellerResponse || "");
    setIsReviewResponseModalOpen(true);
  };

  const handleUpdateShop = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShop) return;

    setIsSubmitting(true);
    try {
      const shopRef = doc(firestoreDb, "shops", selectedShop.id);
      const updatedData = {
        name: shopForm.name,
        description: shopForm.description,
        whatsapp: shopForm.whatsapp,
        website: shopForm.website,
        email: shopForm.email,
        instagram: shopForm.instagram,
        facebook: shopForm.facebook,
        logoURL: shopForm.logoURL,
        bannerURL: shopForm.bannerURL,
        type: shopForm.type,
        categories: shopForm.categories,
        mallZone: shopForm.mallZone,
        isIntegral: shopForm.isIntegral,
        businessHours: shopForm.businessHours,
        isClubAffiliated: shopForm.isClubAffiliated,
        location: {
          ...selectedShop.location,
          city: shopForm.city,
          country: shopForm.country,
          address: shopForm.address
        },
        customDomain: shopForm.customDomain,
        physicalLocation: shopForm.physicalLocation,
        premiumPhotos: shopForm.premiumPhotos.split(",").map(p => p.trim()).filter(p => p !== "")
      };

      await setDoc(shopRef, updatedData, { merge: true });
      toast.success("Tienda actualizada correctamente");
      setIsEditShopModalOpen(false);
      fetchShops(); // Refresh shops list
    } catch (error) {
      console.error("Error updating shop:", error);
      toast.error("Error al actualizar la tienda");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditShopModal = () => {
    if (!selectedShop) return;
    setShopForm({
      name: selectedShop.name,
      description: selectedShop.description,
      whatsapp: selectedShop.whatsapp || "",
      website: selectedShop.website || "",
      email: selectedShop.email || "",
      instagram: selectedShop.instagram || "",
      facebook: selectedShop.facebook || "",
      logoURL: selectedShop.logoURL || "",
      bannerURL: selectedShop.bannerURL || "",
      city: selectedShop.location.city,
      country: selectedShop.location.country,
      address: selectedShop.location.address || "",
      type: selectedShop.type,
      categories: selectedShop.categories || [],
      mallZone: selectedShop.mallZone || "otros",
      isClubAffiliated: selectedShop.isClubAffiliated || false,
      isIntegral: selectedShop.isIntegral || false,
      businessHours: selectedShop.businessHours || DEFAULT_BUSINESS_HOURS,
      customDomain: selectedShop.customDomain || "",
      physicalLocation: selectedShop.physicalLocation || { lat: 0, lng: 0, address: "" },
      premiumPhotos: (selectedShop.premiumPhotos || []).join(", ")
    });
    setIsEditShopModalOpen(true);
  };

  useEffect(() => {
    // Auto-select ONLY if nothing is selected and there's exactly one shop
    if (shops.length === 1 && !selectedShop && profile?.role !== "admin") {
      setSelectedShop(shops[0]);
    } else if (selectedShop) {
      // Sync selectedShop if data in shops list changed
      const refreshed = shops.find(s => s.id === selectedShop.id);
      if (refreshed && JSON.stringify(refreshed) !== JSON.stringify(selectedShop)) {
        setSelectedShop(refreshed);
      }
    }
  }, [shops, selectedShop, profile]);

  useEffect(() => {
    if (profile?.role === "admin" && !selectedShop) {
      if (activeTab === "dashboard" || activeTab === "products" || activeTab === "orders" || activeTab === "settings" || activeTab === "crm") {
        setActiveTab("admin_panel");
      }
    }
  }, [profile, selectedShop, activeTab]);

  useEffect(() => {
    const init = async () => {
      if (!isAuthReady) return;
      if (!user) {
        navigate("/login");
        return;
      }
      
      setLoading(true);
      await fetchShops();
      
      if (profile) {
        setUserProfileForm({
          displayName: profile.displayName || "",
          phoneNumber: profile.phoneNumber || "",
          city: profile.location?.city || "",
          country: profile.location?.country || ""
        });
        if (profile.role === "admin") {
          fetchAdminData();
        }
      }
      
      setLoading(false);
    };
    init();

    // Listen for buyer meetings
    if (user) {
      const buyerMeetingsQuery = query(collection(firestoreDb, "meetings"), where("buyerUid", "==", user.uid));
      const unsubscribeBuyerMeetings = onSnapshot(buyerMeetingsQuery, (snapshot) => {
        const meetingsData = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as Meeting));
        setBuyerMeetings(meetingsData.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()));
      });
      return () => unsubscribeBuyerMeetings();
    }
  }, [user, isAuthReady, profile]);

  useEffect(() => {
    if (selectedShop) {
      console.log("Setting up real-time meeting listener for shop:", selectedShop.id);
      
      // Initialize Binance Form
      if (selectedShop.binanceSettings) {
        setBinanceForm(selectedShop.binanceSettings);
      } else {
        setBinanceForm({
          merchantId: "",
          apiKey: "",
          secretKey: "",
          active: false
        });
      }

      // Initialize Payment Settings Form
      if (selectedShop.paymentSettings) {
        setPaymentSettingsForm(selectedShop.paymentSettings);
      } else {
        setPaymentSettingsForm({
          zelle: { email: "", name: "", active: false },
          bizum: { phone: "", active: false },
          bank: { bankName: "", accountHolder: "", accountNumber: "", active: false },
          stripe: { active: false },
          paypal: { active: false, email: "" },
        });
      }

      // Initialize Invoice Form
      if (selectedShop.invoiceSettings) {
        setInvoiceForm(selectedShop.invoiceSettings);
      } else {
        setInvoiceForm({
          taxId: "",
          businessName: "",
          resolutionNumber: "",
          resolutionDate: "",
          prefix: "",
          fromNumber: 1,
          toNumber: 999999,
          active: false,
          footerText: ""
        });
      }

      const meetingsQuery = query(collection(firestoreDb, "meetings"), where("shopId", "==", selectedShop.id));
      const unsubscribeMeetings = onSnapshot(meetingsQuery, (snapshot) => {
        const meetingsData = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as Meeting));
        setMeetings(meetingsData.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()));
      });

      fetchProducts(selectedShop.id);
      fetchSales(selectedShop.id);
      fetchShopIntegration(selectedShop.id);
      fetchChats();
      fetchCRM(selectedShop.id);

      return () => {
        unsubscribeMeetings();
      };
    }
    fetchPurchases();
  }, [selectedShop, user]);

  useEffect(() => {
    if (selectedChat) {
      fetchMessages(selectedChat.id);
    }
  }, [selectedChat]);

  const handleUpdateBinanceSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShop) return;

    setIsSubmitting(true);
    try {
      await updateDoc(doc(firestoreDb, "shops", selectedShop.id), {
        binanceSettings: binanceForm,
        updatedAt: new Date().toISOString()
      });
      toast.success("Configuración de Binance Pay actualizada");
      fetchShops();
    } catch (error: any) {
      toast.error("Error al actualizar Binance Pay: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePaymentSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShop) return;

    setIsSubmitting(true);
    try {
      await updateDoc(doc(firestoreDb, "shops", selectedShop.id), {
        paymentSettings: paymentSettingsForm,
        updatedAt: new Date().toISOString()
      });
      toast.success("Métodos de pago actualizados");
      fetchShops();
    } catch (error: any) {
      toast.error("Error al actualizar pagos: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateInvoiceSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShop) return;

    setIsSubmitting(true);
    try {
      await updateDoc(doc(firestoreDb, "shops", selectedShop.id), {
        invoiceSettings: invoiceForm,
        updatedAt: new Date().toISOString()
      });
      toast.success("Configuración de Facturación Digital actualizada");
      fetchShops();
    } catch (error: any) {
      toast.error("Error al actualizar facturación: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadVCard = () => {
    if (!selectedShop) return;
    
    // Create vCard content
    const vcard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${selectedShop.name}`,
      `ORG:${selectedShop.name}`,
      "TITLE:Tienda en EmpreMarket",
      `TEL;TYPE=WORK,VOICE:${selectedShop.whatsapp || ""}`,
      `EMAIL;TYPE=PREF,INTERNET:${selectedShop.email || ""}`,
      `URL:${window.location.origin}/shop/${selectedShop.id}`,
      `ADR;TYPE=WORK:;;${selectedShop.location.address || ""};${selectedShop.location.city};;${selectedShop.location.country}`,
      `NOTE:${selectedShop.description || ""}`,
      "END:VCARD"
    ].join("\n");

    const blob = new Blob([vcard], { type: "text/vcard" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${selectedShop.name.replace(/\s+/g, "_")}_contact.vcf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Tarjeta de contacto (vCard) generada con éxito");
  };

  const handleCreateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedShop) return;

    const price = parseFloat(productForm.price);
    const stock = parseInt(productForm.stock);

    setIsSubmitting(true);
    try {
      const userPlan = profile?.subscription?.tier || "free";
      const planLimits = PRICING_PLANS.find(p => p.id === userPlan)?.limits;
      const maxProducts = planLimits?.maxProducts || 10;

      if (products.length >= maxProducts) {
        toast.error(`Has alcanzado el límite de ${maxProducts} productos de tu plan ${userPlan.toUpperCase()}`);
        setIsSubmitting(false);
        return;
      }

      const productData = {
        shopId: selectedShop.id,
        name: productForm.name,
        description: productForm.description,
        price: price,
        priceToAgree: productForm.priceToAgree,
        images: productForm.image ? [productForm.image] : [],
        stock: isNaN(stock) ? 0 : stock,
        category: productForm.category,
        isClubExclusive: productForm.isClubExclusive,
        type: productForm.type,
        rating: 5,
        reviewCount: 0,
        createdAt: new Date().toISOString(),
        tags: productForm.tags.split(",").map(t => t.trim()).filter(t => t !== "")
      };

      await addDoc(collection(firestoreDb, "products"), productData);
      toast.success("Producto creado exitosamente");
      setIsAddModalOpen(false);
      setProductForm({
        name: "",
        description: "",
        price: "",
        priceToAgree: false,
        category: CATEGORIES[0],
        isClubExclusive: false,
        type: "product",
        image: "",
        stock: "10",
        tags: ""
      });
      fetchProducts(selectedShop.id);
    } catch (error: any) {
      toast.error("Error al crear el producto: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProduct || !selectedShop) return;

    setIsSubmitting(true);
    try {
      const productData = {
        ...editingProduct,
        name: productForm.name,
        description: productForm.description,
        price: parseFloat(productForm.price),
        priceToAgree: productForm.priceToAgree,
        stock: parseInt(productForm.stock),
        category: productForm.category,
        isClubExclusive: productForm.isClubExclusive,
        type: productForm.type,
        images: productForm.image ? [productForm.image] : editingProduct.images,
        tags: productForm.tags.split(",").map(t => t.trim()).filter(t => t !== "")
      };

      const { id, ...dataToUpdate } = productData;
      await setDoc(doc(firestoreDb, "products", id), dataToUpdate, { merge: true });
      
      toast.success("Producto actualizado");
      setIsEditModalOpen(false);
      fetchProducts(selectedShop.id);
    } catch (error: any) {
      toast.error("Error al actualizar: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      priceToAgree: product.priceToAgree || false,
      category: product.category,
      isClubExclusive: product.isClubExclusive || false,
      type: product.type,
      image: product.images[0] || "",
      stock: (product.stock || 0).toString(),
      tags: product.tags?.join(", ") || ""
    });
    setIsEditModalOpen(true);
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar este producto?")) return;
    
    try {
      await deleteDoc(doc(firestoreDb, "products", productId));
      toast.success("Producto eliminado");
      if (selectedShop) fetchProducts(selectedShop.id);
    } catch (error: any) {
      toast.error("Error al eliminar: " + error.message);
    }
  };

  if (loading || !isAuthReady) {
    return (
      <div className="container py-12 px-4 md:px-8">
        <div className="flex items-center justify-between mb-8">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-6 md:grid-cols-4 mb-8">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 w-full" />)}
        </div>
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (shops.length === 0 && profile?.role !== "admin") {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center p-8 rounded-3xl bg-muted/30 border border-dashed border-muted-foreground/20">
          <div className="h-20 w-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <Store className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Aún no tienes una tienda</h1>
          <p className="text-muted-foreground mb-8">
            Para empezar a vender tus productos y servicios, primero debes crear tu vitrina digital. Únete al plan Premium para desbloquear herramientas Pro.
          </p>
          <Button size="lg" className="w-full h-12 text-lg" onClick={() => navigate("/become-seller")}>
            Crear mi primera tienda
          </Button>
        </div>
      </div>
    );
  }

  if (shops.length > 0 && !selectedShop && (profile?.role as string) !== "admin") {
    const isSuperAdmin = (profile?.role as string) === "admin";
    const filteredShops = shops.filter(shop => {
      if (!isSuperAdmin) return true;
      const q = shopSelectionSearch.toLowerCase().trim();
      if (!q) return true;
      return (
        (shop.name || "").toLowerCase().includes(q) ||
        (shop.location?.city || "").toLowerCase().includes(q) ||
        (shop.location?.country || "").toLowerCase().includes(q) ||
        ((shop as any).category || "").toLowerCase().includes(q) ||
        (shop.ownerUid || "").toLowerCase().includes(q) ||
        (shop.id || "").toLowerCase().includes(q)
      );
    });

    return (
      <div className="container py-12 px-4 md:px-8 max-w-5xl mx-auto animate-fade-in">
        <div className="text-center mb-12">
          {isSuperAdmin && (
            <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 px-4 py-1.5 rounded-full text-red-600 text-xs font-black uppercase tracking-widest shadow-sm mb-4">
              <ShieldCheck className="h-4 w-4 shrink-0 text-red-600" />
              Modo Super Administrador - Filtro Global
            </div>
          )}
          <h1 className="text-4xl font-black mb-4 tracking-tight">
            {isSuperAdmin ? "Control de Ecosistema" : "Tus Negocios"}
          </h1>
          <p className="text-muted-foreground text-lg">
            {isSuperAdmin 
              ? "Selecciona cualquiera de las tiendas activas en la plataforma para gestionarla en profundidad con privilegios de administrador." 
              : "Selecciona la tienda que deseas administrar hoy."}
          </p>
        </div>

        {isSuperAdmin && (
          <div className="mb-10 max-w-md mx-auto relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-red-500" />
            <Input 
              placeholder="Buscar tienda por nombre, dueño (UID), id o ciudad..."
              value={shopSelectionSearch}
              onChange={(e) => setShopSelectionSearch(e.target.value)}
              className="pl-11 rounded-[20px] h-12 border-red-500/10 focus-visible:ring-red-500 shadow-sm text-sm"
            />
          </div>
        )}
        
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredShops.map((shop) => (
            <Card 
              key={shop.id} 
              className={`group cursor-pointer hover:shadow-2xl hover:border-primary/50 transition-all duration-300 rounded-[32px] overflow-hidden bg-white border ${isSuperAdmin ? 'border-red-500/10 shadow-sm' : 'border-primary/5'}`}
              onClick={() => {
                setSelectedShop(shop);
                setActiveTab("dashboard");
                if (isSuperAdmin) {
                  toast.success(`Administrando tienda: ${shop.name}`);
                }
              }}
            >
              <CardHeader className="p-6 pb-2">
                <div className={`h-12 w-12 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform ${isSuperAdmin ? 'bg-red-500/10 text-red-600' : 'bg-primary/10 text-primary'}`}>
                  <Store className="h-6 w-6" />
                </div>
                <CardTitle className="text-xl font-bold group-hover:text-primary transition-colors">{shop.name}</CardTitle>
                <CardDescription className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> {shop.location?.city || "S/N"}, {shop.location?.country || "Varios"}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div className="mt-4 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-widest">{(shop as any).category || (shop as any).mallZone || 'Tienda'}</Badge>
                    {shop.plan === 'premium' && (
                      <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-none text-[10px]">
                        PREMIUM
                      </Badge>
                    )}
                  </div>
                  {isSuperAdmin && (
                    <div className="text-[10px] text-muted-foreground font-mono bg-slate-50 border p-2 rounded-xl mt-2 overflow-hidden truncate">
                      <div className="truncate" title={shop.ownerUid}>Propietario UID: {shop.ownerUid}</div>
                      <div className="truncate" title={shop.id}>ID Tienda: {shop.id}</div>
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter className="p-6 pt-0 mt-auto">
                <Button 
                  className={`w-full rounded-2xl h-11 font-bold group-hover:bg-primary group-hover:text-white transition-colors ${isSuperAdmin ? 'bg-red-600 hover:bg-red-700 text-white' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedShop(shop);
                    setActiveTab("dashboard");
                    if (isSuperAdmin) {
                      toast.success(`Administrando tienda: ${shop.name}`);
                    }
                  }}
                >
                  {isSuperAdmin ? "Gestionar como Admin" : "Gestionar Negocio"}
                </Button>
              </CardFooter>
            </Card>
          ))}
          
          {!isSuperAdmin && (
            <Card 
              className="border-dashed border-2 cursor-pointer hover:bg-slate-50 transition-colors rounded-[32px] flex flex-col items-center justify-center p-8 text-center min-h-[250px]"
              onClick={() => navigate("/become-seller")}
            >
              <div className="h-12 w-12 rounded-full border-dashed border-2 flex items-center justify-center mb-4 text-muted-foreground">
                <Plus className="h-6 w-6" />
              </div>
              <p className="font-bold">Nueva Tienda</p>
              <p className="text-xs text-muted-foreground">Expande tu imperio comercial</p>
            </Card>
          )}
        </div>
      </div>
    );
  }

  const PremiumWall = ({ title, description }: { title: string, description: string }) => (
    <div className="flex flex-col items-center justify-center p-12 text-center bg-muted/10 border border-dashed rounded-3xl min-h-[400px]">
      <div className="h-16 w-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-orange-200">
        <Zap className="h-8 w-8 text-white fill-white" />
      </div>
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <p className="text-muted-foreground max-w-sm mb-6 text-sm">{description}</p>
      <Button onClick={() => setActiveTab("premium")} className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 border-none shadow-md">
        Activar Plan Premium
      </Button>
    </div>
  );

  return (
    <div className="container py-12 px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight">
              {!selectedShop ? "Consola de Super Administrador" : "Panel de Vendedor"}
            </h1>
            {!selectedShop ? (
              <p className="text-muted-foreground mt-2">
                Gestión y control del ecosistema completo de Mall Empremarket de manera global.
              </p>
            ) : (
              <div className="flex flex-wrap items-center gap-3 mt-2">
                <div className="flex items-center gap-2 bg-slate-50 border rounded-2xl px-3 py-1 text-sm shadow-sm">
                  <Store className="h-4 w-4 text-primary" />
                  <span className="font-bold">{selectedShop.name}</span>
                  <Separator orientation="vertical" className="h-4 mx-1" />
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 px-2 text-[10px] font-black uppercase text-muted-foreground hover:text-primary transition-colors"
                    onClick={() => setSelectedShop(null)}
                  >
                    Cambiar Negocio
                  </Button>
                </div>
                
                {selectedShop.plan === 'premium' && (
                  <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-none gap-1 py-1">
                    <Zap className="h-3 w-3 fill-white" />
                    Premium
                  </Badge>
                )}
                <Button variant="ghost" size="sm" onClick={() => navigate("/become-seller")} className="text-primary h-auto p-0 hover:bg-transparent">
                  <Plus className="h-4 w-4 mr-1" /> Crear otra tienda
                </Button>
              </div>
            )}
          </div>
          {selectedShop ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => navigate(`/shop/${selectedShop.id}`)} className="w-full sm:w-auto">
                Ver mi vitrina
              </Button>
              <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 w-full sm:w-auto">
                    <Plus className="h-4 w-4" /> Nuevo Producto
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Añadir Nuevo Producto</DialogTitle>
                    <DialogDescription>
                      Completa la información para subir tu producto o servicio al catálogo.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateProduct} className="space-y-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="name">Nombre del producto</Label>
                      <Input 
                        id="name" 
                        placeholder="Ej: Jabón Artesanal de Lavanda" 
                        value={productForm.name}
                        onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                        required 
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="price">Precio ($)</Label>
                        <Input 
                          id="price" 
                          type="number" 
                          placeholder="0.00" 
                          value={productForm.price}
                          onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                          required={!productForm.priceToAgree}
                          disabled={productForm.priceToAgree}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="stock">Stock inicial</Label>
                        <Input 
                          id="stock" 
                          type="number" 
                          placeholder="10" 
                          value={productForm.stock}
                          onChange={(e) => setProductForm({...productForm, stock: e.target.value})}
                          required 
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        id="priceToAgree" 
                        checked={productForm.priceToAgree}
                        onChange={(e) => setProductForm({...productForm, priceToAgree: e.target.checked, price: e.target.checked ? "0" : productForm.price})}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <Label htmlFor="priceToAgree" className="text-sm font-medium cursor-pointer">
                        Precio a convenir (no mostrar precio fijo)
                      </Label>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="category">Categoría</Label>
                      <select 
                        id="category"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        value={productForm.category}
                        onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                      >
                        {CATEGORIES.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2 py-2">
                      <input 
                        id="isClubExclusive"
                        type="checkbox" 
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        checked={productForm.isClubExclusive}
                        onChange={(e) => setProductForm({...productForm, isClubExclusive: e.target.checked})}
                      />
                      <Label htmlFor="isClubExclusive" className="text-sm font-medium cursor-pointer">
                        Producto exclusivo para socios del Club
                      </Label>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="tags">Etiquetas (separadas por coma)</Label>
                      <Input 
                        id="tags" 
                        placeholder="Ej: orgánico, artesanal, regalo" 
                        value={productForm.tags}
                        onChange={(e) => setProductForm({...productForm, tags: e.target.value})}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="type">Tipo</Label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 text-sm">
                          <input 
                            type="radio" 
                            name="type" 
                            value="product" 
                            checked={productForm.type === "product"}
                            onChange={() => setProductForm({...productForm, type: "product"})}
                          /> Producto
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input 
                            type="radio" 
                            name="type" 
                            value="service" 
                            checked={productForm.type === "service"}
                            onChange={() => setProductForm({...productForm, type: "service"})}
                          /> Servicio
                        </label>
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="image">URL de Imagen</Label>
                      <Input 
                        id="image" 
                        placeholder="https://ejemplo.com/imagen.jpg" 
                        value={productForm.image}
                        onChange={(e) => setProductForm({...productForm, image: e.target.value})}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="description">Descripción</Label>
                      <Textarea 
                        id="description" 
                        placeholder="Describe las características de tu product..." 
                        value={productForm.description}
                        onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                        required 
                      />
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={isSubmitting} className="w-full">
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Creando...
                          </>
                        ) : "Crear Producto"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          ) : (
            profile?.role === "admin" && (
              <Button onClick={fetchAdminData} variant="outline" className="rounded-xl border-red-500/20 text-red-600 hover:bg-red-500/5 font-bold gap-2">
                <ShieldCheck className="h-4 w-4 text-red-600 shrink-0" /> Refrescar Servidor Global
              </Button>
            )
          )}
        </div>

      {/* Stats Overview */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ventas Totales</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${orders.filter(o => o.status !== "cancelled").reduce((acc, o) => acc + o.total, 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">Ingresos brutos acumulados</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pedidos</CardTitle>
            <ShoppingBag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{orders.length}</div>
            <p className="text-xs text-muted-foreground">
              {orders.filter(o => o.status === "pending").length} pendientes de procesar
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Productos</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{products.length}</div>
            <p className="text-xs text-muted-foreground">Activos en catálogo</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Mensajes</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">0</div>
            <p className="text-xs text-muted-foreground">Consultas sin leer</p>
          </CardContent>
        </Card>
      </div>

      <Tabs 
        value={activeTab} 
        onValueChange={(value) => {
          setActiveTab(value);
          setIsSidebarOpen(false); // Contraer panel en móvil al seleccionar
          if (value === "crm" && selectedShop) fetchCRM(selectedShop.id);
          if (value === "chats") fetchChats();
          if (value === "promotions" && selectedShop) fetchPromotions(selectedShop.id);
          if (value === "admin_panel") fetchAdminData();
        }} 
        className="flex flex-col md:flex-row gap-6 items-start"
      >
        <aside className="w-full md:w-64 shrink-0 md:sticky md:top-20 z-10 transition-all duration-300">
          <div className="md:hidden mb-4">
            <Button 
              variant="outline" 
              className="w-full justify-between h-12 rounded-xl shadow-sm bg-white"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              <div className="flex items-center gap-2">
                <Menu className="h-4 w-4 text-primary" />
                <span className="font-bold">
                  {activeTab === "dashboard" && "Resumen"}
                  {activeTab === "products" && "Productos"}
                  {activeTab === "orders" && "Ventas"}
                  {activeTab === "purchases" && "Mis Compras"}
                  {activeTab === "reviews" && "Reseñas"}
                  {activeTab === "chats" && "Mensajes"}
                  {activeTab === "crm" && "Clientes"}
                  {activeTab === "promotions" && "Fidelización"}
                  {activeTab === "scheduling" && "Agenda & Meet"}
                  {activeTab === "invoicing" && "Facturación"}
                  {activeTab === "funnels" && "Embudos"}
                  {activeTab === "coupons" && "Cupones"}
                  {activeTab === "automations" && "Automatización"}
                  {activeTab === "courses" && "Mis Cursos"}
                  {activeTab === "blog" && "Blog"}
                  {activeTab === "affiliates" && "Afiliados"}
                  {activeTab === "certifications" && "Certificaciones"}
                  {activeTab === "calculator" && "Calculadora"}
                  {activeTab === "settings" && "Configuración"}
                  {activeTab === "subscription" && "Mi Plan"}
                  {activeTab === "shop_select_tab" && "Seleccionar Tienda"}
                  {activeTab === "admin_panel" && "Super Administrador"}
                </span>
              </div>
              {isSidebarOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
          
          <div className={`${isSidebarOpen ? 'block' : 'hidden'} md:block transition-all`}>
            <TabsList className="flex flex-col h-auto w-full bg-muted/30 p-2 gap-1 rounded-xl border shadow-sm">
              {profile?.role === 'admin' && (
                <TabsTrigger 
                  value="admin_panel" 
                  onClick={() => setIsSidebarOpen(false)}
                  className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-red-500 data-[state=active]:text-white data-[state=active]:shadow-sm transition-all text-red-600 font-bold"
                >
                  <ShieldCheck className="h-4 w-4 mr-2" /> Super Usuario
                </TabsTrigger>
              )}

              {!selectedShop && profile?.role === 'admin' && (
                <TabsTrigger 
                  value="shop_select_tab" 
                  onClick={() => setIsSidebarOpen(false)}
                  className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all text-primary font-bold animate-pulse"
                >
                  <Store className="h-4 w-4 mr-2" /> Seleccionar Tienda
                </TabsTrigger>
              )}

              {selectedShop ? (
                <>
                  <div className="px-3 py-1 bg-primary/5 rounded-lg text-[9px] text-primary font-extrabold uppercase tracking-widest text-center my-1 truncate">
                    Tienda: {selectedShop.name}
                  </div>
                  <TabsTrigger 
                    value="dashboard" 
                    onClick={() => setIsSidebarOpen(false)}
                    className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all group"
                  >
                    <LayoutDashboard className="h-4 w-4 mr-2" /> Resumen
                  </TabsTrigger>
                  <TabsTrigger 
                    value="products" 
                    onClick={() => setIsSidebarOpen(false)}
                    className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all"
                  >
                    <Package className="h-4 w-4 mr-2" /> Productos
                  </TabsTrigger>
                  <TabsTrigger 
                    value="orders" 
                    onClick={() => setIsSidebarOpen(false)}
                    className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all"
                  >
                    <ShoppingBag className="h-4 w-4 mr-2" /> Ventas
                  </TabsTrigger>
                  <TabsTrigger 
                    value="reviews" 
                    onClick={() => setIsSidebarOpen(false)}
                    className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all group"
                  >
                    <Star className="h-4 w-4 mr-2" /> Reseñas {(profile?.subscription?.tier === 'free') && <Badge variant="outline" className="ml-auto text-[8px] h-4 px-1.5 opacity-50 group-hover:opacity-100 italic">PRO</Badge>}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="crm" 
                    onClick={() => setIsSidebarOpen(false)}
                    className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all group"
                  >
                    <Users className="h-4 w-4 mr-2" /> Clientes {(profile?.subscription?.tier === 'free') && <Badge variant="outline" className="ml-auto text-[8px] h-4 px-1.5 opacity-50 group-hover:opacity-100 italic">PRO</Badge>}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="promotions" 
                    onClick={() => setIsSidebarOpen(false)}
                    className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all group"
                  >
                    <Award className="h-4 w-4 mr-2" /> Fidelización {(profile?.subscription?.tier === 'free') && <Badge variant="outline" className="ml-auto text-[8px] h-4 px-1.5 opacity-50 group-hover:opacity-100 italic">PRO</Badge>}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="scheduling" 
                    onClick={() => setIsSidebarOpen(false)}
                    className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all relative group"
                  >
                    <Calendar className="h-4 w-4 mr-2" /> Agenda & Meet {!(profile?.subscription?.tier === 'webinar' || profile?.subscription?.tier === 'unlimited') && <Badge variant="outline" className="ml-auto text-[8px] h-4 px-1.5 opacity-50 group-hover:opacity-100 italic">PRO</Badge>}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="invoicing" 
                    onClick={() => setIsSidebarOpen(false)}
                    className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all group"
                  >
                    <FileText className="h-4 w-4 mr-2" /> Facturación {!(profile?.subscription?.tier === 'webinar' || profile?.subscription?.tier === 'unlimited') && <Badge variant="outline" className="ml-auto text-[8px] h-4 px-1.5 opacity-50 group-hover:opacity-100 italic">PRO</Badge>}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="funnels" 
                    onClick={() => setIsSidebarOpen(false)}
                    className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all group"
                  >
                    <Filter className="h-4 w-4 mr-2" /> Embudos {!(profile?.subscription?.tier === 'webinar' || profile?.subscription?.tier === 'unlimited') && <Badge variant="outline" className="ml-auto text-[8px] h-4 px-1.5 opacity-50 group-hover:opacity-100 italic">PRO</Badge>}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="coupons" 
                    onClick={() => setIsSidebarOpen(false)}
                    className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all group"
                  >
                    <CreditCard className="h-4 w-4 mr-2" /> Cupones {(profile?.subscription?.tier === 'free') && <Badge variant="outline" className="ml-auto text-[8px] h-4 px-1.5 opacity-50 group-hover:opacity-100 italic">PRO</Badge>}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="automations" 
                    onClick={() => setIsSidebarOpen(false)}
                    className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all group"
                  >
                    <Workflow className="h-4 w-4 mr-2" /> Automatización {!(profile?.subscription?.tier === 'webinar' || profile?.subscription?.tier === 'unlimited') && <Badge variant="outline" className="ml-auto text-[8px] h-4 px-1.5 opacity-50 group-hover:opacity-100 italic">PRO</Badge>}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="settings" 
                    onClick={() => setIsSidebarOpen(false)}
                    className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all"
                  >
                    <Settings className="h-4 w-4 mr-2" /> Ajustes Tienda
                  </TabsTrigger>
                </>
              ) : null}

              <div className="px-3 py-1 text-[9px] text-muted-foreground uppercase tracking-widest font-extrabold border-t border-b/50 my-1">
                Servicios Personales
              </div>

              <TabsTrigger 
                value="purchases" 
                onClick={() => setIsSidebarOpen(false)}
                className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all"
              >
                <Package className="h-4 w-4 mr-2" /> Mis Compras
              </TabsTrigger>
              <TabsTrigger 
                value="chats" 
                onClick={() => setIsSidebarOpen(false)}
                className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all"
              >
                <MessageSquare className="h-4 w-4 mr-2" /> Mensajes
              </TabsTrigger>
              <TabsTrigger 
                value="courses" 
                onClick={() => setIsSidebarOpen(false)}
                className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all group"
              >
                <GraduationCap className="h-4 w-4 mr-2" /> Mis Cursos
              </TabsTrigger>
              <TabsTrigger 
                value="calculator" 
                onClick={() => setIsSidebarOpen(false)}
                className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all group"
              >
                <TrendingUp className="h-4 w-4 mr-2" /> Calculadora Finanzas
              </TabsTrigger>
              <TabsTrigger 
                value="profile" 
                onClick={() => setIsSidebarOpen(false)}
                className="w-full justify-start px-4 py-2.5 text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all"
              >
                <User className="h-4 w-4 mr-2" /> Mi Perfil
              </TabsTrigger>
            </TabsList>


          <div className="mt-4 p-4 border rounded-xl bg-slate-50 space-y-3">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-widest">
              <HelpCircle className="h-3 w-3" /> Soporte
            </div>
            <Button variant="link" className="p-0 h-auto text-xs text-slate-600 justify-start w-full">
              Centro de Ayuda
            </Button>
            <Button variant="link" className="p-0 h-auto text-xs text-slate-600 justify-start w-full">
              Contactar Soporte
            </Button>
            <Separator />
            <Button variant="ghost" size="sm" className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => navigate("/")}>
              <LogOut className="h-3 w-3 mr-2" /> Salir del Panel
            </Button>
          </div>

          <Button 
            variant="ghost" 
            size="sm" 
            className="md:hidden w-full mt-2 text-slate-400"
            onClick={() => setIsSidebarOpen(false)}
          >
            <X className="h-4 w-4 mr-2" /> Cerrar Panel
          </Button>
        </div>
      </aside>
        
        <div className="flex-1 w-full min-w-0 space-y-4">
          <TabsContent value="shop_select_tab" className="mt-0 space-y-6">
            <div className="bg-white rounded-[32px] p-6 border shadow-sm animate-fade-in">
              <div className="text-center mb-8">
                <div className="h-14 w-14 bg-red-500/10 text-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Store className="h-7 w-7" />
                </div>
                <h2 className="text-2xl font-black mb-2">Selección de Negocio</h2>
                <p className="text-muted-foreground text-sm max-w-lg mx-auto">
                  Como administrador global, puedes seleccionar y gestionar en tiempo real cualquiera de las vitrinas o tiendas publicadas en el ecosistema.
                </p>
              </div>
              
              <div className="mb-8 max-w-md mx-auto relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-red-500" />
                <Input 
                  placeholder="Buscar tienda por nombre o ID..."
                  value={shopSelectionSearch}
                  onChange={(e) => setShopSelectionSearch(e.target.value)}
                  className="pl-11 rounded-[20px] h-12 border-red-500/10 focus-visible:ring-red-500 shadow-sm text-sm"
                />
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {shops
                  .filter(shop => {
                    const q = shopSelectionSearch.toLowerCase().trim();
                    if (!q) return true;
                    return (
                      (shop.name || "").toLowerCase().includes(q) ||
                      (shop.location?.city || "").toLowerCase().includes(q) ||
                      (shop.location?.country || "").toLowerCase().includes(q) ||
                      ((shop as any).category || "").toLowerCase().includes(q) ||
                      (shop.ownerUid || "").toLowerCase().includes(q) ||
                      (shop.id || "").toLowerCase().includes(q)
                    );
                  })
                  .map((shop) => (
                    <Card 
                      key={shop.id} 
                      className="group cursor-pointer hover:shadow-lg hover:border-red-500/30 transition-all duration-300 rounded-[24px] overflow-hidden bg-white border border-red-500/10 shadow-sm flex flex-col justify-between"
                      onClick={() => {
                        setSelectedShop(shop);
                        setActiveTab("dashboard");
                        toast.success(`Administrando tienda: ${shop.name}`);
                      }}
                    >
                      <CardHeader className="p-5 pb-2">
                        <div className="h-10 w-10 rounded-xl flex items-center justify-center mb-3 bg-red-500/10 text-red-600 transition-transform group-hover:scale-105">
                          <Store className="h-5 w-5" />
                        </div>
                        <CardTitle className="text-lg font-bold group-hover:text-primary transition-colors">{shop.name}</CardTitle>
                        <CardDescription className="flex items-center gap-1 text-xs">
                          <MapPin className="h-3 w-3" /> {shop.location?.city || "S/N"}, {shop.location?.country || "Varios"}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="p-5 pt-0 text-xs flex-grow">
                        <div className="flex items-center justify-between mt-2">
                          <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{(shop as any).category || (shop as any).mallZone || 'Tienda'}</Badge>
                          {shop.plan === 'premium' && (
                            <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-none text-[8px]">
                              PREMIUM
                            </Badge>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground font-mono bg-slate-50 border p-2 rounded-lg mt-3 overflow-hidden truncate">
                          <div className="truncate">Dueño: {shop.ownerUid}</div>
                        </div>
                      </CardContent>
                      <CardFooter className="p-5 pt-0">
                        <Button 
                          className="w-full rounded-xl h-10 font-bold bg-red-600 hover:bg-red-700 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedShop(shop);
                            setActiveTab("dashboard");
                            toast.success(`Administrando tienda: ${shop.name}`);
                          }}
                        >
                          Gestionar como Admin
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="dashboard" className="mt-0 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Welcome & Quick Stats */}
              <div className="space-y-6">
                <Card className="rounded-[32px] border-none shadow-xl bg-slate-900 text-white overflow-hidden relative">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 blur-[100px] rounded-full translate-x-1/2 -translate-y-1/2" />
                  <CardHeader>
                    <CardTitle className="text-3xl font-black">¡Hola, {(profile?.displayName || "Emprendedor").split(" ")[0]}!</CardTitle>
                    <CardDescription className="text-slate-400">Hoy es una gran oportunidad para escalar {selectedShop?.name}.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-3xl bg-white/5 border border-white/10">
                        <p className="text-xs text-slate-400 uppercase font-black tracking-widest mb-1">Ventas Hoy</p>
                        <p className="text-2xl font-black">${(orders || []).filter(o => typeof o.createdAt === 'string' && o.createdAt.startsWith(new Date().toISOString().split('T')[0])).reduce((acc, o) => acc + (Number(o.total) || 0), 0).toLocaleString()}</p>
                      </div>
                      <div className="p-4 rounded-3xl bg-white/5 border border-white/10">
                        <p className="text-xs text-slate-400 uppercase font-black tracking-widest mb-1">Nuevos Leads</p>
                        <p className="text-2xl font-black">{(crmClients || []).filter(c => typeof c.createdAt === 'string' && c.createdAt.startsWith(new Date().toISOString().split('T')[0])).length}</p>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="pt-0">
                    <Button className="w-full bg-primary hover:bg-primary/90 text-white border-none rounded-2xl h-12 font-bold" onClick={() => navigate(`/shop/${selectedShop?.id}`)}>
                      <Eye className="h-4 w-4 mr-2" /> Ver mi vitrina pública
                    </Button>
                  </CardFooter>
                </Card>

                {/* Pending Tasks / Notifications */}
                <Card className="rounded-[32px] shadow-sm border-primary/5">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold flex items-center justify-between">
                      Pendiente de Atención
                      <Badge variant="outline" className="text-primary border-primary/20">
                        {((orders || []).filter(o => o.status === "pending").length) + ((meetings || []).filter(m => m.status === "pending").length)} Tareas
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(orders || []).filter(o => o.status === "pending").length > 0 && (
                      <div className="flex items-center justify-between p-4 bg-orange-50 border border-orange-100 rounded-2xl">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                            <ShoppingBag className="h-5 w-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-sm font-bold">Tienes pedidos pendientes</p>
                            <p className="text-xs text-orange-600 font-medium">{(orders || []).filter(o => o.status === "pending").length} órdenes esperan ser procesadas</p>
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" className="hover:bg-orange-100 text-orange-700 font-bold" onClick={() => setActiveTab("orders")}>Gestionar</Button>
                      </div>
                    )}
                    {(meetings || []).filter(m => m.status === "pending").length > 0 && (
                      <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-100 rounded-2xl">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Calendar className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-bold">Solicitudes de Cita</p>
                            <p className="text-xs text-blue-600 font-medium">{(meetings || []).filter(m => m.status === "pending").length} clientes quieren agendar hoy</p>
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" className="hover:bg-blue-100 text-blue-700 font-bold" onClick={() => setActiveTab("scheduling")}>Revisar</Button>
                      </div>
                    )}
                    {(orders || []).filter(o => o.status === "pending").length === 0 && (meetings || []).filter(m => m.status === "pending").length === 0 && (
                      <div className="text-center py-8">
                        <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3 opacity-20" />
                        <p className="text-sm font-medium text-slate-500">¡Todo al día!</p>
                        <p className="text-xs text-slate-400">No tienes tareas administrativas urgentes.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Recent Leads / CRM summary */}
              <div className="space-y-6">
                <Card className="rounded-[32px] shadow-sm border-primary/5 h-full">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-xl font-bold">Últimos Clientes</CardTitle>
                      <CardDescription>Potenciales ventas recientes captadas por tu CRM.</CardDescription>
                    </div>
                    <Button variant="ghost" size="sm" className="font-bold text-primary" onClick={() => setActiveTab("crm")}>Ver todos</Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {crmClients.length === 0 ? (
                      <div className="text-center py-12 border border-dashed rounded-3xl">
                        <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-xs font-bold text-slate-400">Sin clientes registrados aún</p>
                      </div>
                    ) : (
                      crmClients.slice(0, 5).map((client, i) => (
                        <div key={i} className="flex items-center justify-between p-3 border-b last:border-0 hover:bg-slate-50 transition-colors rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-black text-primary italic text-xs">
                              {client.name ? client.name[0] : '?'}
                            </div>
                            <div>
                              <p className="text-sm font-bold">{client.name}</p>
                              <p className="text-[10px] text-muted-foreground">{client.email}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-[8px] uppercase tracking-widest font-black">
                            {client.stage}
                          </Badge>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Quick Shortcuts */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Button variant="outline" className="h-24 rounded-3xl border-dashed flex flex-col gap-2 hover:bg-primary/5 hover:border-primary/50 transition-all" onClick={() => setIsAddModalOpen(true)}>
                <Plus className="h-6 w-6 text-primary" />
                <span className="text-xs font-bold">Añadir Producto</span>
              </Button>
              <Button variant="outline" className="h-24 rounded-3xl border-dashed flex flex-col gap-2 hover:bg-primary/5 hover:border-primary/50 transition-all" onClick={() => setActiveTab("coupons")}>
                <Ticket className="h-6 w-6 text-primary" />
                <span className="text-xs font-bold">Crear Cupón</span>
              </Button>
              <Button variant="outline" className="h-24 rounded-3xl border-dashed flex flex-col gap-2 hover:bg-primary/5 hover:border-primary/50 transition-all" onClick={() => setActiveTab("blog")}>
                <Newspaper className="h-6 w-6 text-primary" />
                <span className="text-xs font-bold">Publicar Blog</span>
              </Button>
              <Button variant="outline" className="h-24 rounded-3xl border-dashed flex flex-col gap-2 hover:bg-primary/5 hover:border-primary/50 transition-all" onClick={() => setActiveTab("settings")}>
                <Settings className="h-6 w-6 text-primary" />
                <span className="text-xs font-bold">Ajustes Tienda</span>
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="products" className="mt-0 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Mi Catálogo</CardTitle>
              <CardDescription>Gestiona tus productos y servicios disponibles.</CardDescription>
            </CardHeader>
            <CardContent>
              {products.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Package className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">Aún no tienes productos</h3>
                  <p className="text-muted-foreground mb-6">Empieza a subir tus creaciones para que el mundo las vea.</p>
                  <Button className="gap-2" onClick={() => setIsAddModalOpen(true)}>
                    <Plus className="h-4 w-4" /> Subir mi primer producto
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <Button className="gap-2" onClick={() => setIsAddModalOpen(true)}>
                      <Plus className="h-4 w-4" /> Nuevo Producto
                    </Button>
                  </div>
                  <div className={`grid gap-4 ${products.length > 5 ? "max-h-[500px] overflow-y-auto pr-2" : ""}`}>
                    {products
                      .slice((productsPage - 1) * ITEMS_PER_PAGE, productsPage * ITEMS_PER_PAGE)
                      .map((product) => (
                      <div key={product.id} className="flex items-center justify-between p-4 border rounded-xl bg-background hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-4">
                          <div className="h-16 w-16 rounded-lg overflow-hidden bg-muted">
                            <img 
                              src={product.images[0] || "https://picsum.photos/seed/product/200/200"} 
                              alt={product.name}
                              className="h-full w-full object-cover"
                              referrerPolicy="no-referrer"
                            />
                          </div>
                          <div>
                            <h4 className="font-bold">{product.name}</h4>
                            <div className="flex items-center gap-3 text-sm text-muted-foreground">
                              <span>{product.priceToAgree ? "Precio a convenir" : `$${product.price.toLocaleString()}`}</span>
                              <span>•</span>
                              <span>Stock: {product.stock || 0}</span>
                              <span>•</span>
                              <Badge variant="outline" className="text-[10px] uppercase">{product.type}</Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => openEditModal(product)}>
                            <Edit className="h-4 w-4 mr-2" /> Editar
                          </Button>
                          <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => handleDeleteProduct(product.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {products.length > ITEMS_PER_PAGE && (
                    <div className="flex items-center justify-between pt-4 border-t">
                      <p className="text-xs text-muted-foreground">
                        Mostrando {(productsPage - 1) * ITEMS_PER_PAGE + 1} - {Math.min(productsPage * ITEMS_PER_PAGE, products.length)} de {products.length} productos
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          disabled={productsPage === 1}
                          onClick={() => setProductsPage(prev => prev - 1)}
                        >
                          <ChevronLeft className="h-4 w-4" /> Anterior
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          disabled={productsPage * ITEMS_PER_PAGE >= products.length}
                          onClick={() => setProductsPage(prev => prev + 1)}
                        >
                          Siguiente <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="orders">
          <Card>
            <CardHeader>
              <CardTitle>Ventas Recientes</CardTitle>
              <CardDescription>Gestiona las compras realizadas por tus clientes.</CardDescription>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <ShoppingBag className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">No hay ventas registradas</h3>
                  <p className="text-muted-foreground">Cuando alguien compre tus productos, aparecerán aquí.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className={`space-y-4 ${orders.length > 5 ? "max-h-[500px] overflow-y-auto pr-2" : ""}`}>
                    {orders
                      .slice((ordersPage - 1) * ITEMS_PER_PAGE, ordersPage * ITEMS_PER_PAGE)
                      .map((order) => (
                      <div key={order.id} className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-xl gap-4 bg-background">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                            <ShoppingBag className="h-6 w-6 text-muted-foreground" />
                          </div>
                          <div>
                            <div className="font-bold">Venta #{order.id.slice(-6).toUpperCase()}</div>
                            <div className="text-sm text-muted-foreground">
                              {new Date(order.createdAt).toLocaleDateString()} • {order.items.length} productos
                            </div>
                            <div className="text-[10px] font-bold text-primary mt-1 flex items-center gap-1 uppercase">
                              <CreditCard className="h-3 w-3" /> {order.paymentMethod}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <div className="font-bold">${order.total.toLocaleString()}</div>
                            <Badge variant={order.status === "completed" ? "default" : "secondary"}>
                              {ORDER_STATUSES[order.status as keyof typeof ORDER_STATUSES]?.label || order.status}
                            </Badge>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => window.open(`https://wa.me/${selectedShop?.whatsapp}?text=Hola! Consulto por la venta #${order.id.slice(-6).toUpperCase()}`, "_blank")}>
                            Contactar Cliente
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {orders.length > ITEMS_PER_PAGE && (
                    <div className="flex items-center justify-between pt-4 border-t">
                      <p className="text-xs text-muted-foreground">
                        Página {ordersPage} de {Math.ceil(orders.length / ITEMS_PER_PAGE)}
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          disabled={ordersPage === 1}
                          onClick={() => setOrdersPage(prev => prev - 1)}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          disabled={ordersPage * ITEMS_PER_PAGE >= orders.length}
                          onClick={() => setOrdersPage(prev => prev + 1)}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="purchases">
          <Card>
            <CardHeader>
              <CardTitle>Mis Compras</CardTitle>
              <CardDescription>Historial de pedidos que has realizado a otras tiendas.</CardDescription>
            </CardHeader>
            <CardContent>
              {purchases.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <ShoppingBag className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">No has realizado compras</h3>
                  <p className="text-muted-foreground">Tus pedidos a otros emprendedores aparecerán aquí.</p>
                  <Button asChild className="mt-4" variant="outline">
                    <Link to="/shops">Explorar Tiendas</Link>
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className={`space-y-4 ${purchases.length > 5 ? "max-h-[600px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-muted-foreground/20" : ""}`}>
                    {purchases
                      .slice((purchasesPage - 1) * ITEMS_PER_PAGE, purchasesPage * ITEMS_PER_PAGE)
                      .map((order) => (
                      <div key={order.id} className="flex flex-col lg:flex-row lg:items-center justify-between p-4 border rounded-xl gap-4 bg-background">
                        <div className="flex flex-col gap-4">
                          <div className="flex items-center gap-4">
                            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Package className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                              <div className="font-bold font-mono text-sm uppercase">Pedido #{order.id.slice(-6)}</div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(order.createdAt).toLocaleDateString()} • {order.items.length} productos
                              </div>
                              <div className="text-[10px] font-bold text-primary mt-1 flex items-center gap-1 uppercase">
                                <CreditCard className="h-3 w-3" /> {order.paymentMethod}
                              </div>
                            </div>
                          </div>
                          
                          {/* List of products in the order */}
                          <div className="grid gap-2 ml-4 pl-4 border-l-2 border-muted">
                            {order.items.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-3">
                                <img 
                                  src={item.image} 
                                  alt={item.name} 
                                  className="h-8 w-8 rounded object-cover shadow-sm"
                                  referrerPolicy="no-referrer"
                                />
                                <div className="text-[11px]">
                                  <Link to={`/product/${item.productId}`} className="font-medium hover:underline">
                                    {item.name}
                                  </Link>
                                  <span className="text-muted-foreground ml-2">x{item.quantity}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <div className="font-bold text-lg">${order.total.toLocaleString()}</div>
                            <Badge variant="secondary" className={`uppercase text-[10px] ${order.status === "completed" ? "bg-green-100 text-green-700" : ""}`}>
                              {ORDER_STATUSES[order.status as keyof typeof ORDER_STATUSES]?.label || order.status}
                            </Badge>
                          </div>
                          <div className="flex flex-col gap-2 min-w-[140px]">
                            {order.status !== "completed" && (
                              <Button size="sm" className="h-8 text-xs" onClick={() => handleConfirmReceipt(order.id)}>
                                Confirmar Recibido
                              </Button>
                            )}
                            {order.status === "completed" && (
                              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => openReviewModal(order)}>
                                Dejar Reseña
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="h-8 text-xs underline" onClick={() => window.open(`https://wa.me/${(order as any).shopWhatsapp || ''}?text=Hola! Consulto por mi compra #${order.id.slice(-6).toUpperCase()}`, "_blank")}>
                                Contactar Tienda
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 text-[10px]" asChild>
                              <Link to={`/shop/${order.shopId}`}>Ir a la Tienda</Link>
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {purchases.length > ITEMS_PER_PAGE && (
                    <div className="flex items-center justify-between pt-4 border-t">
                      <p className="text-[10px] text-muted-foreground">Mostrando {Math.min(purchasesPage * ITEMS_PER_PAGE, purchases.length)} de {purchases.length}</p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-8 w-8 p-0"
                          disabled={purchasesPage === 1}
                          onClick={() => setPurchasesPage(prev => prev - 1)}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-8 w-8 p-0"
                          disabled={purchasesPage * ITEMS_PER_PAGE >= purchases.length}
                          onClick={() => setPurchasesPage(prev => prev + 1)}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="chats" className="mt-0 w-full">
          <Card className="h-[calc(100vh-250px)] min-h-[500px] flex flex-col overflow-hidden">
            <div className="flex flex-1 overflow-hidden relative">
              {/* Chat List */}
              <div className={`w-full md:w-80 border-r flex flex-col bg-muted/10 absolute md:relative z-10 h-full transition-transform duration-300 bg-background ${selectedChat ? '-translate-x-full md:translate-x-0' : 'translate-x-0'}`}>
                <div className="p-4 border-b bg-background flex items-center justify-between">
                  <h3 className="font-bold flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" /> Conversaciones
                  </h3>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {chats.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground text-sm">
                      No tienes mensajes aún.
                    </div>
                  ) : (
                    <>
                      {chats
                        .slice((chatsPage - 1) * ITEMS_PER_PAGE, chatsPage * ITEMS_PER_PAGE)
                        .map(chat => (
                        <button
                          key={chat.id}
                          onClick={() => setSelectedChat(chat)}
                          className={`w-full p-4 text-left border-b transition-colors hover:bg-muted/50 ${selectedChat?.id === chat.id ? 'bg-primary/5 border-l-4 border-l-primary' : ''}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <UserCircle className="h-6 w-6 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-sm truncate">
                                {(chat as any).participantNames?.[chat.participants.find(p => p !== user?.uid) || ""] || "Cliente"}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">{chat.lastMessage || "Sin mensajes"}</p>
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {new Date(chat.updatedAt).toLocaleDateString()}
                            </div>
                          </div>
                        </button>
                      ))}
                      
                      {chats.length > ITEMS_PER_PAGE && (
                        <div className="p-2 border-t flex items-center justify-between bg-muted/5">
                          <Button 
                            variant="ghost" 
                            size="xs" 
                            disabled={chatsPage === 1}
                            onClick={() => setChatsPage(prev => prev - 1)}
                          >
                            <ChevronLeft className="h-3 w-3 mr-1" /> Ant.
                          </Button>
                          <span className="text-[10px] text-muted-foreground font-medium">Pág. {chatsPage}</span>
                          <Button 
                            variant="ghost" 
                            size="xs" 
                            disabled={chatsPage * ITEMS_PER_PAGE >= chats.length}
                            onClick={() => setChatsPage(prev => prev + 1)}
                          >
                            Sig. <ChevronRight className="h-3 w-3 ml-1" />
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Chat Window */}
              <div className={`flex-1 flex flex-col bg-background w-full h-full ${!selectedChat ? 'hidden md:flex' : 'flex'}`}>
                {selectedChat ? (
                  <>
                    <div className="p-4 border-b flex items-center justify-between bg-muted/5">
                      <div className="flex items-center gap-3">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="md:hidden" 
                          onClick={() => setSelectedChat(null)}
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </Button>
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <UserCircle className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <p className="font-bold text-sm">
                            {(selectedChat as any).participantNames?.[selectedChat.participants.find(p => p !== user?.uid) || ""] || "Conversación"}
                          </p>
                          <p className="text-[10px] text-green-600 flex items-center gap-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> En línea
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="hidden sm:flex gap-2 h-8"
                          onClick={() => {
                            const otherUid = selectedChat.participants.find(p => p !== user?.uid);
                            const name = (selectedChat as any).participantNames?.[otherUid || ""] || "";
                            setNewClientForm({ ...newClientForm, name });
                            setIsAddingClient(true);
                          }}
                        >
                          <UserPlus className="h-4 w-4" /> Guardar en CRM
                        </Button>
                      </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/5">
                      {messages.map(msg => (
                        <div key={msg.id} className={`flex ${msg.senderUid === user?.uid ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] md:max-w-[70%] p-3 rounded-2xl text-sm ${msg.senderUid === user?.uid ? 'bg-primary text-primary-foreground rounded-tr-none' : 'bg-white border rounded-tl-none shadow-sm'}`}>
                            <p className="whitespace-pre-wrap break-words">{msg.text}</p>
                            <p className={`text-[10px] mt-1 ${msg.senderUid === user?.uid ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                              {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <form onSubmit={handleSendMessage} className="p-4 border-t bg-background flex gap-2 sticky bottom-0">
                      <Input 
                        placeholder="Escribe un mensaje..." 
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="flex-1"
                      />
                      <Button type="submit" size="icon" disabled={isSendingMessage || !newMessage.trim()}>
                        {isSendingMessage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </form>
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-8 space-y-4">
                    <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center">
                      <MessageSquare className="h-10 w-10 text-muted-foreground" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">Tus Mensajes</h3>
                      <p className="text-sm text-muted-foreground max-w-xs">
                        Selecciona una conversación de la lista para empezar a chatear con tus clientes.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="crm" className="mt-0 w-full">
          {selectedShop?.plan !== 'premium' ? (
            <PremiumWall 
              title="Gestión de Clientes (CRM)" 
              description="Organiza tus prospectos, lleva el seguimiento de tus ventas y exporta tu base de datos de clientes para campañas de marketing especializadas."
            />
          ) : (
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle>Gestión de Clientes (CRM)</CardTitle>
                    <CardDescription>Sigue el progreso de tus ventas y gestiona la relación con tus clientes.</CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <div className="relative w-full md:w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input 
                        placeholder="Buscar cliente..." 
                        className="pl-9 h-9"
                        value={crmSearchTerm}
                        onChange={(e) => setCrmSearchTerm(e.target.value)}
                      />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          <Download className="h-4 w-4" /> Exportar
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => exportCRM('csv')}>Exportar como CSV</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => exportCRM('txt')}>Exportar como TXT</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button variant="outline" size="sm" onClick={() => selectedShop && fetchCRM(selectedShop.id)} disabled={isLoadingCRM}>
                      <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingCRM ? 'animate-spin' : ''}`} /> Actualizar
                    </Button>
                    <Dialog open={isAddingClient} onOpenChange={setIsAddingClient}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="gap-2">
                          <Plus className="h-4 w-4" /> Añadir Cliente
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Añadir Nuevo Cliente</DialogTitle>
                          <DialogDescription>Registra un nuevo cliente manualmente en tu base de datos.</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleAddClient} className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="clientName">Nombre Completo</Label>
                            <Input 
                              id="clientName" 
                              required 
                              value={newClientForm.name}
                              onChange={(e) => setNewClientForm({...newClientForm, name: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="clientEmail">Correo Electrónico</Label>
                            <Input 
                              id="clientEmail" 
                              type="email" 
                              required 
                              value={newClientForm.email}
                              onChange={(e) => setNewClientForm({...newClientForm, email: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="clientPhone">Teléfono (Opcional)</Label>
                            <Input 
                              id="clientPhone" 
                              value={newClientForm.phone}
                              onChange={(e) => setNewClientForm({...newClientForm, phone: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="clientNotes">Notas Iniciales</Label>
                            <Textarea 
                              id="clientNotes" 
                              placeholder="Añade cualquier información relevante..." 
                              value={newClientForm.notes}
                              onChange={(e) => setNewClientForm({...newClientForm, notes: e.target.value})}
                            />
                          </div>
                          <DialogFooter>
                            <Button type="submit" className="w-full">Guardar Cliente</Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="text-left py-3 px-4 font-medium">Cliente</th>
                      <th className="text-left py-3 px-4 font-medium">Contacto</th>
                      <th className="text-left py-3 px-4 font-medium">Etapa de Venta</th>
                      <th className="text-left py-3 px-4 font-medium">Última Actividad</th>
                      <th className="text-right py-3 px-4 font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {crmClients.filter(c => 
                      c.name.toLowerCase().includes(crmSearchTerm.toLowerCase()) || 
                      c.email.toLowerCase().includes(crmSearchTerm.toLowerCase())
                    ).length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-muted-foreground">
                          {crmSearchTerm ? "No se encontraron clientes con ese criterio." : "No hay clientes registrados aún."}
                        </td>
                      </tr>
                    ) : (
                      crmClients.filter(c => 
                        c.name.toLowerCase().includes(crmSearchTerm.toLowerCase()) || 
                        c.email.toLowerCase().includes(crmSearchTerm.toLowerCase())
                      ).map(client => (
                        <tr key={client.id} className="border-b hover:bg-muted/30 transition-colors">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="h-4 w-4 text-primary" />
                              </div>
                              <span className="font-bold">{client.name}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex flex-col gap-1">
                              <span className="text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> {client.email}</span>
                              {client.phone && <span className="text-xs flex items-center gap-1"><Phone className="h-3 w-3" /> {client.phone}</span>}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <Badge className={
                                client.stage === 'lead' ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' :
                                client.stage === 'contacted' ? 'bg-purple-100 text-purple-700 hover:bg-purple-100' :
                                client.stage === 'negotiating' ? 'bg-orange-100 text-orange-700 hover:bg-orange-100' :
                                client.stage === 'closed_won' ? 'bg-green-100 text-green-700 hover:bg-green-100' :
                                'bg-red-100 text-red-700 hover:bg-red-100'
                              }>
                                {client.stage === 'lead' ? 'Nuevo Lead' :
                                 client.stage === 'contacted' ? 'Contactado' :
                                 client.stage === 'negotiating' ? 'Negociando' :
                                 client.stage === 'closed_won' ? 'Venta Cerrada' : 'Perdido'}
                              </Badge>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-muted-foreground">
                            {new Date(client.lastInteraction).toLocaleDateString()}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="sm">Gestionar</Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Gestionar Cliente: {client.name}</DialogTitle>
                                  <DialogDescription>Actualiza la etapa de venta y añade notas sobre este cliente.</DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="space-y-2">
                                    <Label>Etapa de Venta</Label>
                                    <div className="grid grid-cols-2 gap-2">
                                      {[
                                        { id: 'lead', label: 'Nuevo Lead', color: 'blue' },
                                        { id: 'contacted', label: 'Contactado', color: 'purple' },
                                        { id: 'negotiating', label: 'Negociando', color: 'orange' },
                                        { id: 'closed_won', label: 'Venta Cerrada', color: 'green' },
                                        { id: 'closed_lost', label: 'Perdido', color: 'red' }
                                      ].map(s => (
                                        <Button 
                                          key={s.id}
                                          variant={client.stage === s.id ? 'default' : 'outline'}
                                          size="sm"
                                          className="justify-start gap-2"
                                          onClick={() => handleUpdateCRMStage(client.id, s.id as CRMStage)}
                                        >
                                          <div className={`h-2 w-2 rounded-full bg-${s.color}-500`} />
                                          {s.label}
                                        </Button>
                                      ))}
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Notas del Cliente</Label>
                                    <Textarea placeholder="Escribe notas sobre el progreso de la venta..." defaultValue={client.notes} />
                                  </div>
                                </div>
                                <DialogFooter>
                                  <Button className="w-full">Guardar Cambios</Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          )}
        </TabsContent>

        <TabsContent value="scheduling" className="mt-0 w-full">
          {selectedShop?.plan !== 'premium' ? (
            <PremiumWall 
              title="Reserva de Citas y Video Conferencias" 
              description="Permite que tus clientes agenden citas directamente desde tu tienda. El sistema generará salas de video privadas automáticamente para tus reuniones virtuales."
            />
          ) : (
            <Card>
              <CardHeader className="px-4 md:px-6">
                <CardTitle>Agenda y Reuniones Virtuales</CardTitle>
                <CardDescription>Gestiona tus reuniones y citas.</CardDescription>
              </CardHeader>
              <CardContent className="px-4 md:px-6 space-y-6">
                <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-xl border border-primary/10">
                  <Video className="h-6 w-6 text-primary" />
                  <div>
                    <p className="text-sm font-bold">Sala de Video Interna</p>
                    <p className="text-xs text-muted-foreground">Tus citas aprobadas generarán una sala privada automáticamente.</p>
                  </div>
                </div>

                <div className="grid gap-8 md:grid-cols-2">
                  {/* Seller Side */}
                  <div className="space-y-4">
                    <h3 className="font-bold flex items-center gap-2 text-primary">
                      <Users className="h-4 w-4" /> Citas de mis Clientes
                    </h3>
                    {meetings.length === 0 ? (
                      <div className="text-center py-10 text-muted-foreground border rounded-xl bg-muted/5 text-sm">
                        No tienes solicitudes de clientes.
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className={`space-y-4 ${meetings.length > 5 ? "max-h-[500px] overflow-y-auto pr-2" : ""}`}>
                          {meetings
                            .slice((meetingsPage - 1) * ITEMS_PER_PAGE, meetingsPage * ITEMS_PER_PAGE)
                            .map((meeting) => (
                            <div key={meeting.id} className="p-4 border rounded-2xl bg-background shadow-sm space-y-3">
                              <div className="flex justify-between items-start gap-2">
                                <div>
                                    <p className="font-bold text-sm">{meeting.title}</p>
                                    <p className="text-[10px] text-muted-foreground">{meeting.buyerName}</p>
                                </div>
                                <Badge variant="secondary" className={`text-[10px] uppercase ${meeting.status !== 'pending' ? 'bg-green-100 text-green-700' : ''}`}>
                                    {meeting.status === 'pending' ? 'Nueva' : meeting.status === 'scheduled' ? 'Confirmada' : meeting.status}
                                </Badge>
                              </div>
                              <div className="text-[10px] flex items-center gap-2 text-muted-foreground">
                                <Calendar className="h-3 w-3" /> {new Date(meeting.startTime).toLocaleDateString()}
                                <Clock className="h-3 w-3" /> {new Date(meeting.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                              
                              {meeting.status === 'pending' && (
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    className="h-8 text-[10px] flex-1 bg-green-600 hover:bg-green-700 text-white border-none" 
                                    onClick={() => handleUpdateMeetingStatus(meeting.id, 'scheduled')}
                                  >
                                    Aceptar
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="h-8 text-[10px] flex-1" 
                                    onClick={() => handleUpdateMeetingStatus(meeting.id, 'rejected')}
                                  >
                                    Rechazar
                                  </Button>
                                </div>
                              )}
                              
                              {meeting.status === 'scheduled' && meeting.meetLink && (
                                <Button 
                                  size="sm" 
                                  className="w-full h-8 text-[10px] gap-1 shadow-sm text-white" 
                                  onClick={() => navigate(meeting.meetLink!)}
                                >
                                  <Video className="h-3 w-3" /> Entrar a la Sala
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>

                        {meetings.length > ITEMS_PER_PAGE && (
                          <div className="flex items-center justify-between pt-4 border-t">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              disabled={meetingsPage === 1}
                              onClick={() => setMeetingsPage(prev => prev - 1)}
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-[10px] text-muted-foreground">Pág. {meetingsPage}</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              disabled={meetingsPage * ITEMS_PER_PAGE >= meetings.length}
                              onClick={() => setMeetingsPage(prev => prev + 1)}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-bold flex items-center gap-2 text-blue-600">
                      <ShoppingBag className="h-4 w-4" /> Mis Citas Solicitadas
                    </h3>
                    {buyerMeetings.length === 0 ? (
                      <div className="text-center py-10 text-muted-foreground border rounded-xl bg-muted/5 text-sm">
                        No has solicitado citas a otras tiendas.
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className={`space-y-4 ${buyerMeetings.length > 5 ? "max-h-[500px] overflow-y-auto pr-2" : ""}`}>
                          {buyerMeetings
                            .slice((buyerMeetingsPage - 1) * ITEMS_PER_PAGE, buyerMeetingsPage * ITEMS_PER_PAGE)
                            .map((meeting) => (
                            <div key={meeting.id} className="p-4 border rounded-2xl bg-background shadow-sm space-y-3">
                               <div className="flex justify-between items-start gap-2">
                                <p className="font-bold text-sm">{meeting.title}</p>
                                <Badge variant="outline" className="text-[10px] uppercase">{meeting.status}</Badge>
                              </div>
                              <div className="text-[10px] flex items-center gap-2 text-muted-foreground">
                                <Calendar className="h-3 w-3" /> {new Date(meeting.startTime).toLocaleDateString()}
                                <Clock className="h-3 w-3" /> {new Date(meeting.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                              {meeting.status === 'scheduled' && meeting.meetLink && (
                                <Button 
                                  size="sm" 
                                  className="w-full h-8 text-[10px] gap-1 bg-blue-600 hover:bg-blue-700 shadow-sm text-white border-none" 
                                  onClick={() => navigate(meeting.meetLink!)}
                                >
                                  <Video className="h-3 w-3" /> Entrar a Videollamada
                                </Button>
                              )}
                              {meeting.status === 'pending' && (
                                <div className="p-2 bg-yellow-50 rounded-lg border border-yellow-100 flex items-center gap-2 text-[10px] text-yellow-700">
                                  <RefreshCw className="h-2.5 w-2.5 animate-spin" /> Esperando aprobación...
                                </div>
                              )}
                            </div>
                          ))}
                        </div>

                        {buyerMeetings.length > ITEMS_PER_PAGE && (
                          <div className="flex items-center justify-between pt-4 border-t">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              disabled={buyerMeetingsPage === 1}
                              onClick={() => setBuyerMeetingsPage(prev => prev - 1)}
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <span className="text-[10px] text-muted-foreground">Pág. {buyerMeetingsPage}</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              disabled={buyerMeetingsPage * ITEMS_PER_PAGE >= buyerMeetings.length}
                              onClick={() => setBuyerMeetingsPage(prev => prev + 1)}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="invoicing" className="mt-0 w-full">
          {selectedShop?.plan !== 'premium' ? (
            <PremiumWall 
              title="Facturación Digital y Legal" 
              description="Emite facturas configuradas según los requisitos de tu ubicación (NIT, RUT, Resolución de facturación). Mantén tu negocio organizado y legal ante las entidades tributarias."
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Configuración de Facturación</CardTitle>
                <CardDescription>Configura los detalles legales para la emisión de tus facturas digitales.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateInvoiceSettings} className="space-y-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="font-bold text-sm text-primary uppercase tracking-wider">Datos de la Empresa</h4>
                      <div className="grid gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="inv-name">Nombre Comercial / Razón Social</Label>
                          <Input 
                            id="inv-name" 
                            placeholder="Nombre como aparece en el registro" 
                            value={invoiceForm.businessName}
                            onChange={(e) => setInvoiceForm({...invoiceForm, businessName: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="inv-taxcode">Identificación Tributaria (NIT/RUT/CUIT)</Label>
                          <Input 
                            id="inv-taxcode" 
                            placeholder="Eje: 900.123.456-1" 
                            value={invoiceForm.taxId}
                            onChange={(e) => setInvoiceForm({...invoiceForm, taxId: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-bold text-sm text-primary uppercase tracking-wider">Resolución Legal</h4>
                      <div className="grid gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="inv-res">Número de Resolución</Label>
                          <Input 
                            id="inv-res" 
                            placeholder="Número oficial de la DIAN/SAT" 
                            value={invoiceForm.resolutionNumber}
                            onChange={(e) => setInvoiceForm({...invoiceForm, resolutionNumber: e.target.value})}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="inv-date">Fecha Resolución</Label>
                            <Input 
                              id="inv-date" 
                              type="date"
                              value={invoiceForm.resolutionDate}
                              onChange={(e) => setInvoiceForm({...invoiceForm, resolutionDate: e.target.value})}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="inv-prefix">Prefijo</Label>
                            <Input 
                              id="inv-prefix" 
                              placeholder="SETT" 
                              value={invoiceForm.prefix}
                              onChange={(e) => setInvoiceForm({...invoiceForm, prefix: e.target.value})}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="font-bold text-sm text-primary uppercase tracking-wider">Rangos de Numeración</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="inv-from">Desde</Label>
                          <Input 
                            id="inv-from" 
                            type="number"
                            value={invoiceForm.fromNumber}
                            onChange={(e) => setInvoiceForm({...invoiceForm, fromNumber: parseInt(e.target.value)})}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="inv-to">Hasta</Label>
                          <Input 
                            id="inv-to" 
                            type="number"
                            value={invoiceForm.toNumber}
                            onChange={(e) => setInvoiceForm({...invoiceForm, toNumber: parseInt(e.target.value)})}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <h4 className="font-bold text-sm text-primary uppercase tracking-wider">Pie de Página</h4>
                      <div className="space-y-2">
                        <Label htmlFor="inv-footer">Texto Adicional</Label>
                        <Textarea 
                          id="inv-footer" 
                          placeholder="Texto legal obligatorio de tu país..." 
                          className="h-20"
                          value={invoiceForm.footerText}
                          onChange={(e) => setInvoiceForm({...invoiceForm, footerText: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 p-4 bg-primary/5 rounded-xl border border-primary/10">
                    <div className="flex items-center h-5">
                      <input
                        id="invoice-active"
                        type="checkbox"
                        className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                        checked={invoiceForm.active}
                        onChange={(e) => setInvoiceForm({...invoiceForm, active: e.target.checked})}
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="invoice-active" className="font-bold text-primary">
                        Activar Facturación Automática
                      </label>
                      <p className="text-muted-foreground">Se generará un PDF legal por cada orden completada.</p>
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-12 text-lg shadow-md" disabled={isSubmitting}>
                    {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
                    Guardar Configuración Legal
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="premium">
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-r from-primary to-blue-600 p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="h-8 w-8 fill-yellow-400 text-yellow-400" />
                <h2 className="text-3xl font-bold">EmpreMarket Premium</h2>
              </div>
              <p className="text-blue-50 max-w-2xl text-lg mb-4">
                Lleva tu negocio al siguiente nivel con herramientas avanzadas de administración, 
                personalización total y presencia web profesional.
              </p>
              {selectedShop?.plan === 'premium' && (
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-xs font-medium backdrop-blur-sm">
                  <Clock className="h-3 w-3" />
                  Suscripción activa hasta: {new Date(selectedShop.subscriptionExpiry!).toLocaleDateString()}
                </div>
              )}
            </div>
            <CardContent className="p-8">
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="space-y-4">
                  <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-bold text-lg">Agenda y CRM Pro</h3>
                  <p className="text-sm text-muted-foreground">
                    Sistema de citas 100% interno con sala de video integrada y CRM avanzado para gestionar tus contactos sin errores de conexión.
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="h-12 w-12 rounded-2xl bg-blue-100 flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-lg">Google Maps Pro</h3>
                  <p className="text-sm text-muted-foreground">
                    Ubicación física exacta con todas las funcionalidades de Google Maps para que tus clientes te encuentren fácilmente.
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="h-12 w-12 rounded-2xl bg-purple-100 flex items-center justify-center">
                    <LayoutDashboard className="h-6 w-6 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-lg">Web Profesional</h3>
                  <p className="text-sm text-muted-foreground">
                    Tu propia página web con dominio personalizado (.com, .net) y hosting incluido durante toda tu suscripción.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <Card className={`relative overflow-hidden border-2 ${selectedShop?.plan === 'premium' ? 'border-muted' : 'border-primary shadow-xl'}`}>
                  {selectedShop?.plan === 'premium' && (
                    <div className="absolute top-0 right-0 bg-muted text-muted-foreground px-4 py-1 text-xs font-bold uppercase">
                      Plan Actual
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle>Plan Mensual</CardTitle>
                    <div className="flex items-baseline gap-1 mt-2">
                      <span className="text-4xl font-bold">$19.99</span>
                      <span className="text-muted-foreground">/mes</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Todas las funciones Premium</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Dominio propio incluido</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Soporte prioritario</li>
                    </ul>
                    <Button 
                      className="w-full" 
                      disabled={selectedShop?.plan === 'premium' || isUpgrading}
                      onClick={() => handleUpgrade('monthly')}
                    >
                      {selectedShop?.plan === 'premium' ? 'Ya eres Premium' : 'Suscribirse Mensual'}
                    </Button>
                  </CardContent>
                </Card>

                <Card className={`relative overflow-hidden border-2 ${selectedShop?.plan === 'premium' ? 'border-muted' : 'border-blue-600 shadow-xl'}`}>
                  <div className="absolute top-0 right-0 bg-blue-600 text-white px-4 py-1 text-xs font-bold uppercase">
                    Ahorra 20%
                  </div>
                  <CardHeader>
                    <CardTitle>Plan Anual</CardTitle>
                    <div className="flex items-baseline gap-1 mt-2">
                      <span className="text-4xl font-bold">$189.99</span>
                      <span className="text-muted-foreground">/año</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Todas las funciones Premium</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Dominio propio incluido</li>
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> 2 meses gratis incluidos</li>
                    </ul>
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700" 
                      disabled={selectedShop?.plan === 'premium' || isUpgrading}
                      onClick={() => handleUpgrade('annual')}
                    >
                      {selectedShop?.plan === 'premium' ? 'Ya eres Premium' : 'Suscribirse Anual'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reviews">
          {selectedShop?.plan !== 'premium' ? (
            <PremiumWall 
              title="Gestión de Reseñas" 
              description="Responde a tus clientes, oculta o destaca opiniones y mantén una reputación impecable. Las reseñas positivas son el alma de tu emprendimiento."
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Gestión de Reseñas</CardTitle>
                <CardDescription>Responde a tus clientes o gestiona los comentarios de tus productos.</CardDescription>
              </CardHeader>
              <CardContent>
                {reviews.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No hay reseñas para mostrar.
                  </div>
                ) : (
                  <div className="space-y-6">
                    {reviews.map(review => (
                      <div key={review.id} className="border rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="font-bold">{review.authorName}</div>
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map(star => (
                                <Star key={star} className={`h-3 w-3 ${star <= review.rating ? "text-yellow-500 fill-yellow-500" : "text-muted"}`} />
                              ))}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(review.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        <p className="text-sm">{review.comment}</p>
                        
                        {review.sellerResponse && (
                          <div className="bg-muted/50 p-3 rounded-md text-sm ml-4 border-l-2 border-primary">
                            <div className="font-semibold mb-1">Tu respuesta:</div>
                            <p>{review.sellerResponse}</p>
                          </div>
                        )}
                        
                        <div className="flex gap-2 justify-end pt-2">
                          <Button 
                            variant={review.isVisible ? "outline" : "default"} 
                            size="sm" 
                            onClick={() => toggleReviewVisibility(review.id, !!review.isVisible)}
                            className={!review.isVisible ? "bg-green-600 hover:bg-green-700 text-white border-none" : ""}
                          >
                            {review.isVisible ? "Ocultar" : "Hacer Visible"}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openResponseModal(review)}>
                            {review.sellerResponse ? "Editar Respuesta" : "Responder"}
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteReview(review.id)}>
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Configuración de Tienda</CardTitle>
              <CardDescription>Administra los detalles y preferencias de tu negocio.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Tarjeta Digital / Marketing */}
              <div className="grid gap-6 p-6 border-2 border-primary/20 rounded-2xl bg-primary/5">
                <div className="flex flex-col md:flex-row gap-6 items-center">
                  <div className="bg-white p-4 rounded-2xl shadow-sm border">
                    <QRCodeSVG 
                      value={`${window.location.origin}/shop/${selectedShop?.id}`} 
                      size={140}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                  <div className="flex-1 text-center md:text-left space-y-3">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full text-[10px] font-bold text-primary uppercase tracking-wider mb-2">
                      <Zap className="h-3 w-3 fill-primary" /> Marketing Digital
                    </div>
                    <h4 className="text-xl font-bold text-primary flex items-center gap-2 justify-center md:justify-start">
                      <CreditCard className="h-5 w-5" />
                      Tu Tarjeta Digital
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Comparte tu negocio fácilmente. Escanea el código QR para entrar a tu tienda o descarga la tarjeta de contacto (vCard) para que tus clientes te guarden.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                      <Button size="sm" className="gap-2" onClick={handleDownloadVCard}>
                        <Download className="h-4 w-4" /> Descargar vCard
                      </Button>
                      <Button size="sm" variant="outline" className="gap-2" onClick={() => {
                        if (selectedShop) {
                          navigator.clipboard.writeText(`${window.location.origin}/shop/${selectedShop.id}`);
                          toast.success("Enlace de la tienda copiado al portapapeles");
                        }
                      }}>
                        <Globe className="h-4 w-4" /> Copiar Enlace
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-2 pt-4">
                <h4 className="font-medium">Perfil de Tienda</h4>
                <p className="text-sm text-muted-foreground">Cambia el logo, banner y descripción de tu negocio.</p>
                <Button variant="outline" className="w-fit" onClick={openEditShopModal}>Editar Perfil</Button>
              </div>
              <div className="grid gap-2">
                <h4 className="font-medium">Gestión de Productos</h4>
                <p className="text-sm text-muted-foreground">Agrega, edita o elimina productos de tu catálogo.</p>
                <Button variant="outline" className="w-fit" onClick={() => setActiveTab("products")}>Administrar Productos</Button>
              </div>
              <div className="grid gap-4 p-4 border rounded-xl bg-muted/5">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold flex items-center gap-2">
                       <img src="https://cryptologos.cc/logos/binance-coin-bnb-logo.png" className="h-4 w-4" alt="Binance" />
                       Binance Pay Settings
                    </h4>
                    <p className="text-xs text-muted-foreground">Configura tus credenciales de Binance Merchant para recibir pagos directos.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="binance-active" className="text-xs">Activo</Label>
                    <input 
                      id="binance-active"
                      type="checkbox" 
                      className="h-4 w-4"
                      checked={binanceForm.active}
                      onChange={(e) => setBinanceForm({...binanceForm, active: e.target.checked})}
                    />
                  </div>
                </div>
                
                <Separator />
                
                <form onSubmit={handleUpdateBinanceSettings} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="merchant-id" className="text-xs">Merchant ID</Label>
                      <Input 
                        id="merchant-id"
                        placeholder="ID de Comerciante de Binance"
                        value={binanceForm.merchantId}
                        onChange={(e) => setBinanceForm({...binanceForm, merchantId: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="api-key" className="text-xs">API Key</Label>
                      <Input 
                        id="api-key"
                        type="password"
                        placeholder="Binance API Key"
                        value={binanceForm.apiKey}
                        onChange={(e) => setBinanceForm({...binanceForm, apiKey: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="secret-key" className="text-xs">Secret Key</Label>
                    <Input 
                      id="secret-key"
                      type="password"
                      placeholder="Binance Secret Key"
                      value={binanceForm.secretKey}
                      onChange={(e) => setBinanceForm({...binanceForm, secretKey: e.target.value})}
                    />
                  </div>
                  <Button type="submit" size="sm" className="w-full bg-[#F3BA2F] text-black hover:bg-[#E2AF2B]" disabled={isSubmitting}>
                    {isSubmitting ? "Guardando..." : "Guardar Configuración de Binance"}
                  </Button>
                </form>
              </div>

              {/* Zelle Settings */}
              <div className="grid gap-4 p-4 border rounded-xl bg-muted/5">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold flex items-center gap-2 text-purple-700">
                       <Zap className="h-4 w-4" />
                       Zelle Settings
                    </h4>
                    <p className="text-xs text-muted-foreground">Configura tu correo electrónico de Zelle para recibir pagos.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="zelle-active" className="text-xs">Activo</Label>
                    <input 
                      id="zelle-active"
                      type="checkbox" 
                      className="h-4 w-4"
                      checked={paymentSettingsForm.zelle?.active}
                      onChange={(e) => setPaymentSettingsForm({
                        ...paymentSettingsForm, 
                        zelle: { ...paymentSettingsForm.zelle!, active: e.target.checked }
                      })}
                    />
                  </div>
                </div>
                <Separator />
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="zelle-email" className="text-xs">Email Zelle</Label>
                    <Input 
                      id="zelle-email"
                      placeholder="tu@email.com"
                      value={paymentSettingsForm.zelle?.email}
                      onChange={(e) => setPaymentSettingsForm({
                        ...paymentSettingsForm, 
                        zelle: { ...paymentSettingsForm.zelle!, email: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zelle-name" className="text-xs">Nombre Completo</Label>
                    <Input 
                      id="zelle-name"
                      placeholder="Nombre del titular"
                      value={paymentSettingsForm.zelle?.name}
                      onChange={(e) => setPaymentSettingsForm({
                        ...paymentSettingsForm, 
                        zelle: { ...paymentSettingsForm.zelle!, name: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              {/* Bizum Settings */}
              <div className="grid gap-4 p-4 border rounded-xl bg-muted/5">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold flex items-center gap-2 text-blue-600">
                       <Phone className="h-4 w-4" />
                       Bizum Settings
                    </h4>
                    <p className="text-xs text-muted-foreground">Configura tu número de teléfono para recibir pagos por Bizum.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="bizum-active" className="text-xs">Activo</Label>
                    <input 
                      id="bizum-active"
                      type="checkbox" 
                      className="h-4 w-4"
                      checked={paymentSettingsForm.bizum?.active}
                      onChange={(e) => setPaymentSettingsForm({
                        ...paymentSettingsForm, 
                        bizum: { ...paymentSettingsForm.bizum!, active: e.target.checked }
                      })}
                    />
                  </div>
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label htmlFor="bizum-phone" className="text-xs">Teléfono Bizum</Label>
                  <Input 
                    id="bizum-phone"
                    placeholder="+34 600 000 000"
                    value={paymentSettingsForm.bizum?.phone}
                    onChange={(e) => setPaymentSettingsForm({
                      ...paymentSettingsForm, 
                      bizum: { ...paymentSettingsForm.bizum!, phone: e.target.value }
                    })}
                  />
                </div>
              </div>

              {/* Bank Settings */}
              <div className="grid gap-4 p-4 border rounded-xl bg-muted/5">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold flex items-center gap-2">
                       <Briefcase className="h-4 w-4" />
                       Cuenta Bancaria
                    </h4>
                    <p className="text-xs text-muted-foreground">Detalles para transferencias bancarias nacionales o internacionales.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="bank-active" className="text-xs">Activo</Label>
                    <input 
                      id="bank-active"
                      type="checkbox" 
                      className="h-4 w-4"
                      checked={paymentSettingsForm.bank?.active}
                      onChange={(e) => setPaymentSettingsForm({
                        ...paymentSettingsForm, 
                        bank: { ...paymentSettingsForm.bank!, active: e.target.checked }
                      })}
                    />
                  </div>
                </div>
                <Separator />
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bank-name" className="text-xs">Banco</Label>
                    <Input 
                      id="bank-name"
                      placeholder="Ej: BBVA, Santander"
                      value={paymentSettingsForm.bank?.bankName}
                      onChange={(e) => setPaymentSettingsForm({
                        ...paymentSettingsForm, 
                        bank: { ...paymentSettingsForm.bank!, bankName: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank-holder" className="text-xs">Titular</Label>
                    <Input 
                      id="bank-holder"
                      placeholder="Nombre del Titular"
                      value={paymentSettingsForm.bank?.accountHolder}
                      onChange={(e) => setPaymentSettingsForm({
                        ...paymentSettingsForm, 
                        bank: { ...paymentSettingsForm.bank!, accountHolder: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bank-number" className="text-xs">Número de Cuenta / IBAN</Label>
                    <Input 
                      id="bank-number"
                      placeholder="Número de cuenta"
                      value={paymentSettingsForm.bank?.accountNumber}
                      onChange={(e) => setPaymentSettingsForm({
                        ...paymentSettingsForm, 
                        bank: { ...paymentSettingsForm.bank!, accountNumber: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleUpdatePaymentSettings} 
                disabled={isSubmitting} 
                className="w-full"
              >
                {isSubmitting ? "Guardando..." : "Guardar Todos los Métodos de Pago"}
              </Button>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-2">
                <h4 className="text-sm font-bold text-amber-800 flex items-center gap-2">
                  <HelpCircle className="h-4 w-4" />
                  Ayuda con Credenciales
                </h4>
                <p className="text-xs text-amber-700">
                  ¿No sabes dónde obtener el <strong>Client Secret</strong> o la <strong>API Key de Google Maps</strong>? 
                  Ve a la pestaña <strong>"Agenda & Meet"</strong> y haz clic en <strong>"Guía de Configuración"</strong> para ver el paso a paso detallado.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calculator" className="mt-0 w-full">
          {selectedShop?.plan !== 'premium' ? (
            <PremiumWall 
              title="Calculadora de Precios Maestra" 
              description="Optimiza tus márgenes de ganancia. Calcula costos de materiales, mano de obra y gastos operativos para poner el precio perfecto a tus productos."
            />
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-primary/10 p-6 rounded-2xl border border-primary/20">
                <div>
                  <h3 className="text-2xl font-bold text-primary flex items-center gap-2">
                    <TrendingUp className="h-6 w-6" /> Calculadora Emprendedora Pro
                  </h3>
                  <p className="text-muted-foreground italic">Domina tus finanzas y pon el precio justo a tu creatividad.</p>
                </div>
                <Button variant="outline" className="gap-2" onClick={() => window.print()}>
                  <Download className="h-4 w-4" /> Exportar PDF
                </Button>
              </div>
              
              <EntrepreneurCalculator />
            </div>
          )}
        </TabsContent>

        <TabsContent value="admin_panel" className="mt-0 space-y-6">
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-red-500/5 border border-red-500/10 p-6 rounded-[24px]">
              <div>
                <h1 className="text-3xl font-black tracking-tight text-red-600 flex items-center gap-2">
                  <ShieldCheck className="h-8 w-8 text-red-600" />
                  Consola de Super Usuario
                </h1>
                <p className="text-muted-foreground text-sm">
                  Gestión integral en tiempo real de perfiles, tiendas, vitrinas y todo el ecosistema de la plataforma.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {adminActiveSubTab === "users" && (
                  <Button 
                    onClick={() => {
                      setAdminUserForm({
                        uid: "user_" + Math.random().toString(36).substring(2, 9),
                        email: "",
                        displayName: "",
                        role: "buyer",
                        phoneNumber: "",
                        birthDate: "",
                        city: "",
                        country: ""
                      });
                      setIsAdminAddUserOpen(true);
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-xl gap-2 font-bold"
                  >
                    <Plus className="h-4 w-4" /> Añadir Usuario
                  </Button>
                )}
                {adminActiveSubTab === "shops" && (
                  <Button 
                    onClick={() => {
                      setAdminShopForm({
                        name: "",
                        description: "",
                        ownerUid: "",
                        whatsapp: "",
                        email: "",
                        city: "",
                        country: "Colombia",
                        address: "",
                        plan: "free"
                      });
                      setSelectedShopForAdmin(null);
                      setIsAdminAddShopOpen(true);
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-xl gap-2 font-bold"
                  >
                    <Plus className="h-4 w-4" /> Registrar Tienda
                  </Button>
                )}
                {adminActiveSubTab === "products" && (
                  <Button 
                    onClick={() => {
                      setAdminProductForm({
                        name: "",
                        description: "",
                        price: "",
                        priceToAgree: false,
                        shopId: allShops[0]?.id || "",
                        category: CATEGORIES[0] || "General",
                        type: "product",
                        isClubExclusive: false,
                        stock: "10"
                      });
                      setSelectedProductForAdmin(null);
                      setIsAdminAddProductOpen(true);
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-xl gap-2 font-bold"
                  >
                    <Plus className="h-4 w-4" /> Añadir Vitrina/Prod
                  </Button>
                )}
                <Button onClick={fetchAdminData} variant="outline" className="rounded-xl border-red-500/20 text-red-600 hover:bg-red-500/5 font-bold">
                  Refrescar Servidor
                </Button>
              </div>
            </div>

            {/* Admin Quick Metrics */}
            <div className="grid gap-4 md:grid-cols-3">
              <Card className="rounded-[24px] border-none shadow-sm bg-slate-50">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-black uppercase text-slate-500 tracking-widest">Total Usuarios</p>
                    <p className="text-3xl font-black mt-1 text-slate-900">{allProfiles.length}</p>
                  </div>
                  <div className="h-12 w-12 rounded-2xl bg-blue-100 flex items-center justify-center text-blue-600">
                    <Users className="h-6 w-6" />
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-[24px] border-none shadow-sm bg-slate-50">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-black uppercase text-slate-500 tracking-widest">Total Tiendas</p>
                    <p className="text-3xl font-black mt-1 text-slate-900">{allShops.length}</p>
                  </div>
                  <div className="h-12 w-12 rounded-2xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                    <Store className="h-6 w-6" />
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-[24px] border-none shadow-sm bg-slate-50">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-black uppercase text-slate-500 tracking-widest">Total Vitrinas/Prod</p>
                    <p className="text-3xl font-black mt-1 text-slate-900">{allProducts.length}</p>
                  </div>
                  <div className="h-12 w-12 rounded-2xl bg-amber-100 flex items-center justify-center text-amber-600">
                    <Package className="h-6 w-6" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Subtabs selection */}
            <div className="flex gap-2 border-b border-muted pb-px overflow-x-auto">
              <Button 
                variant={adminActiveSubTab === "users" ? "default" : "ghost"}
                onClick={() => setAdminActiveSubTab("users")}
                className={`rounded-t-xl rounded-b-none px-6 py-3 font-bold translate-y-px transition-all ${adminActiveSubTab === "users" ? 'bg-red-600 text-white hover:bg-red-700' : ''}`}
              >
                Usuarios ({allProfiles.length})
              </Button>
              <Button 
                variant={adminActiveSubTab === "shops" ? "default" : "ghost"}
                onClick={() => setAdminActiveSubTab("shops")}
                className={`rounded-t-xl rounded-b-none px-6 py-3 font-bold translate-y-px transition-all ${adminActiveSubTab === "shops" ? 'bg-red-600 text-white hover:bg-red-700' : ''}`}
              >
                Tiendas ({allShops.length})
              </Button>
              <Button 
                variant={adminActiveSubTab === "products" ? "default" : "ghost"}
                onClick={() => setAdminActiveSubTab("products")}
                className={`rounded-t-xl rounded-b-none px-6 py-3 font-bold translate-y-px transition-all ${adminActiveSubTab === "products" ? 'bg-red-600 text-white hover:bg-red-700' : ''}`}
              >
                Vitrinas y Productos ({allProducts.length})
              </Button>
            </div>

            {/* Search input to filter instantaneously */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder={`Buscar por nombre, email, id o localidad en ${adminActiveSubTab}...`}
                value={adminSearchQuery}
                onChange={(e) => setAdminSearchQuery(e.target.value)}
                className="pl-10 rounded-xl h-11"
              />
            </div>

            {/* Content Area */}
            <Card className="rounded-3xl border-none shadow-xl overflow-hidden bg-white">
              {adminLoading ? (
                <div className="p-12 text-center text-muted-foreground animate-pulse">
                  Conectando con base de datos de Firebase...
                </div>
              ) : (
                <div className="overflow-x-auto">
                  
                  {/* --- SUB-TAB: USERS LIST --- */}
                  {adminActiveSubTab === "users" && (
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider font-extrabold border-b">
                          <th className="p-4">Usuario</th>
                          <th className="p-4">Rol</th>
                          <th className="p-4">Ubicación</th>
                          <th className="p-4">UID / Teléfono</th>
                          <th className="p-4 text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y text-slate-800 text-sm">
                        {allProfiles
                          .filter(p => {
                            const q = adminSearchQuery.toLowerCase().trim();
                            if (!q) return true;
                            return (
                              (p.displayName || "").toLowerCase().includes(q) ||
                              (p.email || "").toLowerCase().includes(q) ||
                              (p.role || "").toLowerCase().includes(q) ||
                              (p.city || "").toLowerCase().includes(q) ||
                              (p.country || "").toLowerCase().includes(q) ||
                              (p.id || "").toLowerCase().includes(q)
                            );
                          })
                          .map(u => (
                            <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                              <td className="p-4">
                                <div className="font-extrabold text-slate-900">{u.displayName || "Sin nombre"}</div>
                                <div className="text-xs text-muted-foreground">{u.email}</div>
                              </td>
                              <td className="p-4">
                                <Badge className={`uppercase text-[10px] tracking-wide ${u.role === 'admin' ? 'bg-red-500 text-white border-none' : u.role === 'seller' ? 'bg-emerald-500 text-white border-none' : 'bg-slate-500 text-white border-none'}`}>
                                  {u.role || "buyer"}
                                </Badge>
                              </td>
                              <td className="p-4">
                                {u.city || u.country ? (
                                  <span>{u.city ? `${u.city}, ` : ""}{u.country || ""}</span>
                                ) : (
                                  <span className="text-muted-foreground/60 italic text-xs">No especificada</span>
                                )}
                              </td>
                              <td className="p-4">
                                <div className="font-mono text-[10px] text-muted-foreground truncate max-w-[120px]" title={u.id}>{u.id}</div>
                                <div className="text-xs text-slate-600">{u.phoneNumber || u.phone || "Sin Teléfono"}</div>
                              </td>
                              <td className="p-4 text-right space-x-2 whitespace-nowrap">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="rounded-lg h-8"
                                  onClick={() => {
                                    setAdminUserForm({
                                      uid: u.id,
                                      email: u.email || "",
                                      displayName: u.displayName || "",
                                      role: u.role || "buyer",
                                      phoneNumber: u.phoneNumber || u.phone || "",
                                      birthDate: u.birthDate || "",
                                      city: u.city || "",
                                      country: u.country || ""
                                    });
                                    setSelectedUserForAdmin(u);
                                    setIsAdminEditUserOpen(true);
                                  }}
                                >
                                  Editar
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive" 
                                  className="rounded-lg h-8 bg-red-600 hover:bg-red-700 text-white"
                                  onClick={() => handleAdminDeleteUser(u.id)}
                                >
                                  Eliminar
                                </Button>
                              </td>
                            </tr>
                          ))}
                        {allProfiles.length === 0 && (
                          <tr>
                            <td colSpan={5} className="p-8 text-center text-muted-foreground italic">No se han registrado usuarios todavía.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}

                  {/* --- SUB-TAB: SHOPS LIST --- */}
                  {adminActiveSubTab === "shops" && (
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider font-extrabold border-b">
                          <th className="p-4">Tienda</th>
                          <th className="p-4">Plan</th>
                          <th className="p-4">Dueño (UID)</th>
                          <th className="p-4">Contacto</th>
                          <th className="p-4 text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y text-slate-800 text-sm">
                        {allShops
                          .filter(s => {
                            const q = adminSearchQuery.toLowerCase().trim();
                            if (!q) return true;
                            return (
                              (s.name || "").toLowerCase().includes(q) ||
                              (s.description || "").toLowerCase().includes(q) ||
                              (s.location?.city || "").toLowerCase().includes(q) ||
                              (s.location?.country || "").toLowerCase().includes(q) ||
                              (s.id || "").toLowerCase().includes(q) ||
                              (s.ownerUid || "").toLowerCase().includes(q)
                            );
                          })
                          .map(s => (
                            <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                              <td className="p-4">
                                <div className="font-extrabold text-slate-900 flex items-center gap-2">
                                  <Store className="h-4 w-4 text-primary shrink-0" /> {s.name}
                                </div>
                                <div className="text-xs text-muted-foreground font-mono truncate max-w-[150px]" title={s.id}>ID: {s.id}</div>
                                <div className="text-xs text-slate-500 mt-1 italic">{s.location?.city || "Sin ciudad"}, {s.location?.country || "Sin país"}</div>
                              </td>
                              <td className="p-4">
                                <Badge className={`uppercase text-[10px] tracking-wide border-none ${s.plan === 'premium' ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white' : 'bg-slate-200 text-slate-700'}`}>
                                  {s.plan || "free"}
                                </Badge>
                              </td>
                              <td className="p-4">
                                <div className="font-mono text-xs max-w-[150px] truncate">{s.ownerUid}</div>
                                <div className="text-xs text-muted-foreground">{s.email || "Sin email"}</div>
                              </td>
                              <td className="p-4 text-xs font-mono">
                                <div>WS: {s.whatsapp || "S/D"}</div>
                                <div>Mail: {s.email || "S/D"}</div>
                              </td>
                              <td className="p-4 text-right space-x-2 whitespace-nowrap">
                                <Button 
                                  size="sm" 
                                  variant="default" 
                                  className="rounded-lg h-8 bg-blue-600 hover:bg-blue-700 text-white font-bold"
                                  onClick={() => {
                                    setSelectedShop(s);
                                    setActiveTab("dashboard");
                                    toast.success(`Administrando tienda: ${s.name}`);
                                  }}
                                >
                                  Administrar
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="rounded-lg h-8"
                                  onClick={() => {
                                    setAdminShopForm({
                                      name: s.name || "",
                                      description: s.description || "",
                                      ownerUid: s.ownerUid || "",
                                      whatsapp: s.whatsapp || "",
                                      email: s.email || "",
                                      city: s.location?.city || "",
                                      country: s.location?.country || "Colombia",
                                      address: s.location?.address || "",
                                      plan: (s.plan || "free") as any
                                    });
                                    setSelectedShopForAdmin(s);
                                    setIsAdminEditShopOpen(true);
                                  }}
                                >
                                  Editar
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive" 
                                  className="rounded-lg h-8 bg-red-600 hover:bg-red-700 text-white"
                                  onClick={() => handleAdminDeleteShop(s.id)}
                                >
                                  Eliminar
                                </Button>
                              </td>
                            </tr>
                          ))}
                        {allShops.length === 0 && (
                          <tr>
                            <td colSpan={5} className="p-8 text-center text-muted-foreground italic">No se han registrado tiendas todavía.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}

                  {/* --- SUB-TAB: PRODUCTS LIST --- */}
                  {adminActiveSubTab === "products" && (
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider font-extrabold border-b">
                          <th className="p-4">Producto/Vitrina</th>
                          <th className="p-4">Precio</th>
                          <th className="p-4">Categoría</th>
                          <th className="p-4">Tienda asociada (ID)</th>
                          <th className="p-4 text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y text-slate-800 text-sm">
                        {allProducts
                          .filter(p => {
                            const q = adminSearchQuery.toLowerCase().trim();
                            if (!q) return true;
                            return (
                              (p.name || "").toLowerCase().includes(q) ||
                              (p.description || "").toLowerCase().includes(q) ||
                              (p.category || "").toLowerCase().includes(q) ||
                              (p.id || "").toLowerCase().includes(q) ||
                              (p.shopId || "").toLowerCase().includes(q)
                            );
                          })
                          .map(p => (
                             <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                               <td className="p-4">
                                 <div className="font-extrabold text-slate-900 flex items-center gap-2">
                                   <Package className="h-4 w-4 text-primary shrink-0" /> {p.name}
                                 </div>
                                 <div className="text-xs text-muted-foreground font-mono truncate max-w-[150px]" title={p.id}>ID: {p.id}</div>
                                 <div className="text-xs text-slate-500 mt-1 max-w-sm truncate">{p.description}</div>
                                 {p.isClubExclusive && <Badge variant="outline" className="text-[9px] text-red-500 border-red-200 mt-1">Exclusivo Club</Badge>}
                               </td>
                               <td className="p-4 font-bold text-slate-900">
                                 {p.priceToAgree ? (
                                   <span className="text-xs text-emerald-600">A Convenir</span>
                                 ) : (
                                   <span>${(Number(p.price) || 0).toLocaleString()}</span>
                                 )}
                               </td>
                               <td className="p-4">
                                 <Badge variant="secondary" className="uppercase text-[9px] tracking-wide bg-slate-100 text-slate-800 border-none">
                                   {p.category || "General"}
                                 </Badge>
                                 <div className="text-[10px] text-muted-foreground capitalize mt-0.5">Stock: {p.stock ?? "N/A"}</div>
                               </td>
                               <td className="p-4 font-mono text-xs">
                                 <div className="truncate max-w-[150px]">ID: {p.shopId}</div>
                                 <div className="text-muted-foreground text-[10px]">Tienda: {allShops.find(s => s.id === p.shopId)?.name || "No encontrada"}</div>
                               </td>
                               <td className="p-4 text-right space-x-2 whitespace-nowrap">
                                 <Button 
                                   size="sm" 
                                   variant="outline" 
                                   className="rounded-lg h-8"
                                   onClick={() => {
                                     setAdminProductForm({
                                       name: p.name || "",
                                       description: p.description || "",
                                       price: String(p.price || ""),
                                       priceToAgree: !!p.priceToAgree,
                                       shopId: p.shopId || "",
                                       category: p.category || CATEGORIES[0],
                                       type: p.type || "product",
                                       isClubExclusive: !!p.isClubExclusive,
                                       stock: String(p.stock ?? "10")
                                     });
                                     setSelectedProductForAdmin(p);
                                     setIsAdminEditProductOpen(true);
                                   }}
                                 >
                                   Editar
                                 </Button>
                                 <Button 
                                   size="sm" 
                                   variant="destructive" 
                                   className="rounded-lg h-8 bg-red-600 hover:bg-red-700 text-white"
                                   onClick={() => handleAdminDeleteProduct(p.id)}
                                 >
                                   Eliminar
                                 </Button>
                               </td>
                             </tr>
                          ))}
                        {allProducts.length === 0 && (
                          <tr>
                            <td colSpan={5} className="p-8 text-center text-muted-foreground italic">No se han registrado vitrinas o productos todavía.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}

                </div>
              )}
            </Card>
          </div>

          {/* ============================================================================== */}
          {/* MODALS & DIALOGS FOR SUPER-USER ACTIONS                                       */}
          {/* ============================================================================== */}

          {/* 1. EDIT / ADD USER DIALOG */}
          <Dialog open={isAdminEditUserOpen || isAdminAddUserOpen} onOpenChange={(open) => {
            if (!open) {
              setIsAdminEditUserOpen(false);
              setIsAdminAddUserOpen(false);
            }
          }}>
            <DialogContent className="sm:max-w-[500px] bg-white rounded-2xl">
              <DialogHeader>
                <DialogTitle>{isAdminAddUserOpen ? "Añadir Nuevo Usuario" : "Editar Usuario"}</DialogTitle>
                <DialogDescription>
                  Registra o modifica un perfil directamente en la base de datos central de Firestore.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAdminSaveUser} className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="admin-u-uid">UID del Usuario (Único)</Label>
                  <Input 
                    id="admin-u-uid"
                    value={adminUserForm.uid}
                    onChange={(e) => setAdminUserForm({...adminUserForm, uid: e.target.value})}
                    placeholder="Ej: random_uid_321"
                    disabled={isAdminEditUserOpen}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="admin-u-name">Nombre Público</Label>
                    <Input 
                      id="admin-u-name"
                      value={adminUserForm.displayName}
                      onChange={(e) => setAdminUserForm({...adminUserForm, displayName: e.target.value})}
                      placeholder="Nombre"
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="admin-u-email">Email</Label>
                    <Input 
                      id="admin-u-email"
                      type="email"
                      value={adminUserForm.email}
                      onChange={(e) => setAdminUserForm({...adminUserForm, email: e.target.value})}
                      placeholder="email@ejemplo.com"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="admin-u-role">Rol (Privilegios)</Label>
                    <select 
                      id="admin-u-role"
                      value={adminUserForm.role}
                      onChange={(e) => setAdminUserForm({...adminUserForm, role: e.target.value as any})}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="buyer">Comprador (buyer)</option>
                      <option value="seller">Vendedor (seller)</option>
                      <option value="admin">Administrador (admin)</option>
                    </select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="admin-u-phone">Teléfono</Label>
                    <Input 
                      id="admin-u-phone"
                      value={adminUserForm.phoneNumber}
                      onChange={(e) => setAdminUserForm({...adminUserForm, phoneNumber: e.target.value})}
                      placeholder="Teléfono"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="admin-u-city">Ciudad</Label>
                    <Input 
                      id="admin-u-city"
                      value={adminUserForm.city}
                      onChange={(e) => setAdminUserForm({...adminUserForm, city: e.target.value})}
                      placeholder="Ej: Bogotá"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="admin-u-country">País</Label>
                    <Input 
                      id="admin-u-country"
                      value={adminUserForm.country}
                      onChange={(e) => setAdminUserForm({...adminUserForm, country: e.target.value})}
                      placeholder="Ej: Colombia"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setIsAdminEditUserOpen(false); setIsAdminAddUserOpen(false); }}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white font-bold">
                    Guardar Usuario
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* 2. EDIT / ADD SHOP DIALOG */}
          <Dialog open={isAdminEditShopOpen || isAdminAddShopOpen} onOpenChange={(open) => {
            if (!open) {
              setIsAdminEditShopOpen(false);
              setIsAdminAddShopOpen(false);
            }
          }}>
            <DialogContent className="sm:max-w-[500px] bg-white rounded-2xl">
              <DialogHeader>
                <DialogTitle>{selectedShopForAdmin ? "Editar Tienda" : "Añadir Nueva Tienda"}</DialogTitle>
                <DialogDescription>
                  Crea o actualiza tiendas asociadas a cualquier UID de usuario.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAdminSaveShop} className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="admin-s-name">Nombre de la Tienda</Label>
                  <Input 
                    id="admin-s-name"
                    value={adminShopForm.name}
                    onChange={(e) => setAdminShopForm({...adminShopForm, name: e.target.value})}
                    placeholder="Tienda Ejemplo"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="admin-s-desc">Descripción</Label>
                  <textarea
                    id="admin-s-desc"
                    value={adminShopForm.description}
                    onChange={(e) => setAdminShopForm({...adminShopForm, description: e.target.value})}
                    placeholder="Descripción comercial de la tienda"
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="admin-s-owner">Owner UID (Propietario)</Label>
                    <Input 
                      id="admin-s-owner"
                      value={adminShopForm.ownerUid}
                      onChange={(e) => setAdminShopForm({...adminShopForm, ownerUid: e.target.value})}
                      placeholder="Coloca el UID del dueño"
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="admin-s-plan">Plan de la Tienda</Label>
                    <select 
                      id="admin-s-plan"
                      value={adminShopForm.plan}
                      onChange={(e) => setAdminShopForm({...adminShopForm, plan: e.target.value as any})}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="free">Gratuito (free)</option>
                      <option value="premium">Premium (premium)</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="admin-s-whatsapp">WhatsApp</Label>
                    <Input 
                      id="admin-s-whatsapp"
                      value={adminShopForm.whatsapp}
                      onChange={(e) => setAdminShopForm({...adminShopForm, whatsapp: e.target.value})}
                      placeholder="Ej: +573123456789"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="admin-s-email">Email Comercial</Label>
                    <Input 
                      id="admin-s-email"
                      type="email"
                      value={adminShopForm.email}
                      onChange={(e) => setAdminShopForm({...adminShopForm, email: e.target.value})}
                      placeholder="contacto@tienda.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="admin-s-city">Ciudad</Label>
                    <Input 
                      id="admin-s-city"
                      value={adminShopForm.city}
                      onChange={(e) => setAdminShopForm({...adminShopForm, city: e.target.value})}
                      placeholder="Ciudad de la tienda"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="admin-s-country">País</Label>
                    <Input 
                      id="admin-s-country"
                      value={adminShopForm.country}
                      onChange={(e) => setAdminShopForm({...adminShopForm, country: e.target.value})}
                      placeholder="País"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setIsAdminEditShopOpen(false); setIsAdminAddShopOpen(false); }}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white font-bold">
                    {selectedShopForAdmin ? "Guardar Cambios" : "Crear Tienda"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* 3. EDIT / ADD PRODUCT DIALOG */}
          <Dialog open={isAdminEditProductOpen || isAdminAddProductOpen} onOpenChange={(open) => {
            if (!open) {
              setIsAdminEditProductOpen(false);
              setIsAdminAddProductOpen(false);
            }
          }}>
            <DialogContent className="sm:max-w-[500px] bg-white rounded-2xl">
              <DialogHeader>
                <DialogTitle>{selectedProductForAdmin ? "Editar Vitrina/Producto" : "Añadir Nuevo Producto/Vitrina"}</DialogTitle>
                <DialogDescription>
                  Crea o actualiza artículos o servicios vinculándolos a cualquier tienda existente.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAdminSaveProduct} className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="admin-p-name">Nombre del Producto / Servicio</Label>
                  <Input 
                    id="admin-p-name"
                    value={adminProductForm.name}
                    onChange={(e) => setAdminProductForm({...adminProductForm, name: e.target.value})}
                    placeholder="Ej: Zapatillas Deportivas o Consultoría Pro"
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="admin-p-desc">Descripción</Label>
                  <textarea
                    id="admin-p-desc"
                    value={adminProductForm.description}
                    onChange={(e) => setAdminProductForm({...adminProductForm, description: e.target.value})}
                    placeholder="Escribe la descripción de este artículo"
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="admin-p-shop">ID Tienda Asociada</Label>
                    <Input 
                      id="admin-p-shop"
                      value={adminProductForm.shopId}
                      onChange={(e) => setAdminProductForm({...adminProductForm, shopId: e.target.value})}
                      placeholder="Coloca el id de la tienda"
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="admin-p-cat">Categoría</Label>
                    <select 
                      id="admin-p-cat"
                      value={adminProductForm.category}
                      onChange={(e) => setAdminProductForm({...adminProductForm, category: e.target.value})}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                      <option value="General">General</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="admin-p-price">Precio (COP / USD)</Label>
                    <Input 
                      id="admin-p-price"
                      type="number"
                      value={adminProductForm.price}
                      onChange={(e) => setAdminProductForm({...adminProductForm, price: e.target.value})}
                      placeholder="Ej: 45000"
                      disabled={adminProductForm.priceToAgree}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="admin-p-stock">Stock</Label>
                    <Input 
                      id="admin-p-stock"
                      type="number"
                      value={adminProductForm.stock}
                      onChange={(e) => setAdminProductForm({...adminProductForm, stock: e.target.value})}
                      placeholder="10"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4 py-2">
                  <label className="flex items-center gap-2 text-sm font-semibold select-none cursor-pointer">
                    <input 
                      type="checkbox"
                      checked={adminProductForm.priceToAgree}
                      onChange={(e) => setAdminProductForm({...adminProductForm, priceToAgree: e.target.checked})}
                      className="rounded border-input text-red-600 focus:ring-red-500 h-4 w-4"
                    />
                    Precio a Convenir
                  </label>
                  <label className="flex items-center gap-2 text-sm font-semibold select-none cursor-pointer">
                    <input 
                      type="checkbox"
                      checked={adminProductForm.isClubExclusive}
                      onChange={(e) => setAdminProductForm({...adminProductForm, isClubExclusive: e.target.checked})}
                      className="rounded border-input text-red-600 focus:ring-red-500 h-4 w-4"
                    />
                    Exclusivo Club (Fidelización)
                  </label>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setIsAdminEditProductOpen(false); setIsAdminAddProductOpen(false); }}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white font-bold">
                    {selectedProductForAdmin ? "Guardar Cambios" : "Crear Vitrina/Producto"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

        </TabsContent>

        <TabsContent value="profile">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Perfil de Usuario</CardTitle>
                <CardDescription>Gestiona tu información personal y de contacto.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveProfile} className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="profile-name">Nombre Público</Label>
                    <Input 
                      id="profile-name" 
                      value={userProfileForm.displayName}
                      onChange={(e) => setUserProfileForm({...userProfileForm, displayName: e.target.value})}
                      placeholder="Tu nombre o alias"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="profile-phone">Teléfono</Label>
                    <Input 
                      id="profile-phone" 
                      value={userProfileForm.phoneNumber}
                      onChange={(e) => setUserProfileForm({...userProfileForm, phoneNumber: e.target.value})}
                      placeholder="Ej: +54 9 11 2233-4455"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="profile-city">Ciudad</Label>
                      <Input 
                        id="profile-city" 
                        value={userProfileForm.city}
                        onChange={(e) => setUserProfileForm({...userProfileForm, city: e.target.value})}
                        placeholder="Tu ciudad"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="profile-country">País</Label>
                      <select 
                        id="profile-country" 
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        value={userProfileForm.country}
                        onChange={(e) => setUserProfileForm({...userProfileForm, country: e.target.value})}
                      >
                        <option value="" disabled>Selecciona un país</option>
                        {COUNTRIES.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={isSavingProfile}>
                    {isSavingProfile ? "Guardando..." : "Guardar Cambios"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cuenta y Seguridad</CardTitle>
                <CardDescription>Información de tu cuenta en la plataforma.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 p-4 border rounded-xl bg-muted/30">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-bold">{user?.email}</p>
                    <p className="text-xs text-muted-foreground">ID: {user?.uid}</p>
                  </div>
                </div>
                <div className="p-4 border rounded-xl bg-primary/5 border-primary/20 space-y-2">
                  <h4 className="text-sm font-bold flex items-center gap-2 text-primary">
                    <Calendar className="h-4 w-4" />
                    Agenda Interna Activa
                  </h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Tu sistema de agendamiento ahora es 100% interno. Ya no depende de aplicaciones externas, evitando errores de conexión y simplificando tu gestión. Las reuniones se realizan en una sala de video privada dentro de EmpreMarket.
                  </p>
                  <Button variant="link" size="sm" className="p-0 h-auto text-xs text-primary" onClick={() => setActiveTab("scheduling")}>
                    Ir a mi agenda →
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="subscription">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-primary">
                <Zap className="h-5 w-5 fill-primary" />
                Estado de mi suscripción
              </CardTitle>
              <CardDescription>Visualiza el estado de tu cuenta y los límites de tu plan.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="p-6 border rounded-2xl bg-slate-50 flex flex-col items-center text-center shadow-inner">
                  <span className="text-xs font-bold text-muted-foreground uppercase mb-1">Plan Actual</span>
                  <span className="text-2xl font-black text-primary uppercase">{profile?.subscription?.tier || "Gratis"}</span>
                  <Badge className="mt-2 bg-green-100 text-green-800 border-none">Activo</Badge>
                </div>
                <div className="p-6 border rounded-2xl bg-slate-50 col-span-2 shadow-inner">
                  <h4 className="font-bold mb-4 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" /> Uso de recursos
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Tiendas Multinivel</span>
                        <span className="font-bold">{shops.length} / {PRICING_PLANS.find(p => p.id === (profile?.subscription?.tier || "free"))?.limits.maxShops}</span>
                      </div>
                      <div className="h-3 w-full bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary" 
                          style={{ width: `${Math.min(100, (shops.length / (PRICING_PLANS.find(p => p.id === (profile?.subscription?.tier || "free"))?.limits.maxShops || 1)) * 100)}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Productos (Tienda Actual)</span>
                        <span className="font-bold">{products.length} / {PRICING_PLANS.find(p => p.id === (profile?.subscription?.tier || "free"))?.limits.maxProducts}</span>
                      </div>
                      <div className="h-3 w-full bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500" 
                          style={{ width: `${Math.min(100, (products.length / (PRICING_PLANS.find(p => p.id === (profile?.subscription?.tier || "free"))?.limits.maxProducts || 1)) * 100)}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Cursos Online</span>
                        <span className="font-bold">{courses.length} / {PRICING_PLANS.find(p => p.id === (profile?.subscription?.tier || "free"))?.limits.maxCourses}</span>
                      </div>
                      <div className="h-3 w-full bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-purple-500" 
                          style={{ width: `${Math.min(100, (courses.length / (PRICING_PLANS.find(p => p.id === (profile?.subscription?.tier || "free"))?.limits.maxCourses || 1)) * 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold">Mejora tu plan hoy</h3>
                  <Badge variant="outline" className="animate-pulse">Oferta de Lanzamiento</Badge>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {PRICING_PLANS.filter(p => p.id !== profile?.subscription?.tier).map(plan => (
                    <div key={plan.id} className={`p-5 border rounded-2xl flex items-center justify-between hover:border-primary hover:bg-primary/5 transition-all cursor-pointer group shadow-sm`} onClick={() => navigate("/pricing")}>
                      <div>
                        <div className="font-bold flex items-center gap-2">
                          {plan.name} 
                          <span className="text-primary text-sm font-black">${plan.price}/mes</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 underline decoration-primary/30">{plan.features[0]} y mucho más beneficios...</p>
                      </div>
                      <div className="bg-primary/10 p-2 rounded-full group-hover:bg-primary group-hover:text-white transition-colors">
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
            <CardFooter className="bg-slate-50 border-t flex flex-col md:flex-row items-center justify-between p-6 rounded-b-2xl gap-4">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-10 w-10 text-primary p-2 bg-primary/10 rounded-full" />
                <div>
                  <div className="font-bold">Garantía de Satisfacción</div>
                  <div className="text-xs text-muted-foreground">Cancela en cualquier momento. Sin preguntas.</div>
                </div>
              </div>
              <Button size="lg" className="px-8 shadow-lg shadow-primary/20" onClick={() => navigate("/pricing")}>
                Actualizar Ahora <Rocket className="ml-2 h-4 w-4" />
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="funnels">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-primary" />
                Embudos de Venta
              </CardTitle>
              <CardDescription>Crea páginas de aterrizaje optimizadas para convertir visitantes en clientes.</CardDescription>
            </CardHeader>
            <CardContent>
              {!(profile?.subscription?.tier === 'webinar' || profile?.subscription?.tier === 'unlimited') && (
                <div className="p-10 border border-dashed rounded-3xl bg-muted/30 text-center mb-8">
                  <Rocket className="h-12 w-12 text-primary mx-auto mb-4 animate-bounce" />
                  <h3 className="text-2xl font-bold mb-2">Automatiza tus ventas</h3>
                  <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                    Los embudos están disponibles a partir del plan <span className="font-bold text-primary">Webinar</span>. 
                    Crea páginas de captura, ventas y mucho más.
                  </p>
                  <Button size="lg" onClick={() => setActiveTab("subscription")}>Actualizar Plan</Button>
                </div>
              )}

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold">Tus Embudos Activos</h3>
                  <Button disabled={!(profile?.subscription?.tier === 'webinar' || profile?.subscription?.tier === 'unlimited')}>
                    <Plus className="h-4 w-4 mr-2" /> Crear Embudo
                  </Button>
                </div>

                <div className="grid gap-4">
                  {funnels.length === 0 ? (
                    <div className="p-8 border rounded-2xl flex flex-col items-center justify-center text-center bg-slate-50 border-dashed">
                      <MousePointerClick className="h-10 w-10 text-muted-foreground mb-2" />
                      <p className="font-medium">No tienes embudos creados aún.</p>
                      <p className="text-xs text-muted-foreground">Comienza diseñando tu primera página de aterrizaje.</p>
                    </div>
                  ) : (
                    funnels.map(funnel => (
                      <div key={funnel.id} className="p-4 border rounded-xl flex items-center justify-between shadow-sm">
                        <div>
                          <h4 className="font-bold">{funnel.name}</h4>
                          <p className="text-xs text-muted-foreground">/{funnel.slug} • {funnel.steps.length} pasos</p>
                        </div>
                        <Badge variant="outline" className={funnel.active ? "bg-green-50 text-green-700 border-green-200" : ""}>
                          {funnel.active ? "Activo" : "Inactivo"}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="coupons">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  Cupones de Descuento
                </CardTitle>
                <CardDescription>Crea ofertas especiales y códigos promocionales para tus clientes.</CardDescription>
              </div>
              <Button disabled={profile?.subscription?.tier === 'free'}>
                <Plus className="h-4 w-4 mr-2" /> Nuevo Cupón
              </Button>
            </CardHeader>
            <CardContent>
              {profile?.subscription?.tier === 'free' ? (
                <div className="p-12 border border-dashed rounded-3xl text-center flex flex-col items-center bg-slate-50">
                  <CreditCard className="h-12 w-12 text-muted-foreground opacity-20 mb-4" />
                  <h3 className="text-xl font-bold mb-2">Cupones y Descuentos</h3>
                  <p className="text-muted-foreground mb-6 max-w-sm">
                    La gestión de cupones está disponible a partir del plan <span className="font-bold text-primary">Startup</span>. 
                    Incentiva a tus clientes con ofertas por tiempo limitado.
                  </p>
                  <Button size="lg" onClick={() => setActiveTab("subscription")}>Ver Planes PRO</Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 border rounded-2xl bg-slate-50">
                      <p className="text-xs font-bold text-slate-500 uppercase">Cupones Activos</p>
                      <p className="text-2xl font-black">4</p>
                    </div>
                    <div className="p-4 border rounded-2xl bg-slate-50">
                      <p className="text-xs font-bold text-slate-500 uppercase">Uso Total</p>
                      <p className="text-2xl font-black">128</p>
                    </div>
                    <div className="p-4 border rounded-2xl bg-slate-50">
                      <p className="text-xs font-bold text-slate-500 uppercase">Ahorro Generado</p>
                      <p className="text-2xl font-black">$450</p>
                    </div>
                  </div>

                  <div className="border rounded-2xl overflow-hidden">
                    {[
                      { code: "BIENVENIDA20", type: "percent", value: 20, uses: 45, max: 100, active: true },
                      { code: "SUPERVENTA", type: "fixed", value: 10, uses: 83, max: 200, active: true },
                      { code: "BLACKFRIDAY", type: "percent", value: 50, uses: 0, max: 500, active: false },
                    ].map((coupon, i) => (
                      <div key={i} className="flex items-center justify-between p-4 border-b last:border-0 hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="bg-primary/10 p-2 rounded-xl">
                            <Ticket className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-black font-mono text-sm tracking-widest">{coupon.code}</p>
                            <p className="text-[10px] text-muted-foreground">
                              {coupon.type === "percent" ? `${coupon.value}% de descuento` : `$${coupon.value} de descuento fijo`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-xs font-bold">{coupon.uses} / {coupon.max}</p>
                            <p className="text-[8px] text-muted-foreground uppercase font-black tracking-widest">Usos</p>
                          </div>
                          <Badge className={coupon.active ? "bg-green-100 text-green-700 border-green-200" : "bg-slate-100 text-slate-500"}>
                            {coupon.active ? "Vigente" : "Expirado"}
                          </Badge>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                            <Settings className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="automations">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Workflow className="h-5 w-5 text-primary" />
                Automatizaciones
              </CardTitle>
              <CardDescription>Crea reglas automáticas para que tu negocio funcione mientras duermes.</CardDescription>
            </CardHeader>
            <CardContent>
              {!(profile?.subscription?.tier === 'webinar' || profile?.subscription?.tier === 'unlimited') && (
                <div className="p-10 border border-dashed rounded-3xl bg-muted/30 text-center mb-8">
                  <Workflow className="h-12 w-12 text-primary mx-auto mb-4 animate-spin-slow" />
                  <h3 className="text-2xl font-bold mb-2">Pon tu negocio en piloto automático</h3>
                  <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                    Las reglas de automatización están disponibles a partir del plan <span className="font-bold text-primary">Webinar</span>.
                  </p>
                  <Button size="lg" onClick={() => setActiveTab("subscription")}>Actualizar Plan</Button>
                </div>
              )}

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold">Reglas de Automatización</h3>
                  <Button disabled={!(profile?.subscription?.tier === 'webinar' || profile?.subscription?.tier === 'unlimited')}>
                    <Plus className="h-4 w-4 mr-2" /> Nueva Regla
                  </Button>
                </div>

                <div className="grid gap-4">
                  {automationRules.length === 0 ? (
                    <div className="p-12 border rounded-3xl flex flex-col items-center justify-center text-center bg-slate-50">
                      <div className="h-16 w-16 bg-white rounded-full flex items-center justify-center shadow-sm mb-4">
                        <Zap className="h-8 w-8 text-muted-foreground opacity-30" />
                      </div>
                      <p className="font-bold text-lg">No hay reglas activas</p>
                      <p className="text-sm text-muted-foreground max-w-xs">
                        Configura un disparador (ej. nueva venta) y una acción (ej. enviar email de bienvenida).
                      </p>
                    </div>
                  ) : (
                    automationRules.map(rule => (
                      <div key={rule.id} className="p-4 border rounded-xl flex items-center justify-between bg-white shadow-sm ring-1 ring-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <Workflow className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-bold text-sm">Si {rule.trigger} entonces {rule.action}</p>
                            <p className="text-[10px] text-muted-foreground">Creado el {new Date(rule.createdAt).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <Badge className={rule.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}>
                          {rule.active ? "En ejecución" : "Pausada"}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="promotions">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-primary" />
                  Programa de Fidelización
                </CardTitle>
                <CardDescription>Crea beneficios exclusivos para los miembros del Mall Virtual.</CardDescription>
              </div>
              <Dialog open={isPromotionModalOpen} onOpenChange={setIsPromotionModalOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2">
                    <Plus className="h-4 w-4" /> Crear Promoción
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nueva Promoción para el Club</DialogTitle>
                    <DialogDescription>
                      Esta oferta será visible solo para los usuarios con membresía del Mall Virtual.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreatePromotion} className="space-y-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="promo-title">Título de la oferta</Label>
                      <Input 
                        id="promo-title" 
                        placeholder="Ej: 20% de Descuento en Colección de Verano" 
                        value={promotionForm.title}
                        onChange={(e) => setPromotionForm({...promotionForm, title: e.target.value})}
                        required 
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="promo-desc">Descripción</Label>
                      <Textarea 
                        id="promo-desc" 
                        placeholder="Explica detalladamente en qué consiste el beneficio..." 
                        value={promotionForm.description}
                        onChange={(e) => setPromotionForm({...promotionForm, description: e.target.value})}
                        required 
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="promo-code">Código (opcional)</Label>
                        <Input 
                          id="promo-code" 
                          placeholder="CLUB20" 
                          value={promotionForm.discountCode}
                          onChange={(e) => setPromotionForm({...promotionForm, discountCode: e.target.value})}
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="promo-value">Valor (ej: 20% OFF)</Label>
                        <Input 
                          id="promo-value" 
                          placeholder="20% OFF" 
                          value={promotionForm.discountValue}
                          onChange={(e) => setPromotionForm({...promotionForm, discountValue: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label htmlFor="promo-type">Tipo</Label>
                        <select 
                          id="promo-type"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={promotionForm.type}
                          onChange={(e) => setPromotionForm({...promotionForm, type: e.target.value as any})}
                        >
                          <option value="discount">Descuento Directo</option>
                          <option value="event">Invitación a Evento</option>
                          <option value="benefit">Beneficio Exclusivo</option>
                        </select>
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="promo-expiry">Fecha de Caducidad</Label>
                        <Input 
                          id="promo-expiry" 
                          type="date" 
                          value={promotionForm.expiryDate}
                          onChange={(e) => setPromotionForm({...promotionForm, expiryDate: e.target.value})}
                          required 
                        />
                      </div>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="promo-image">Imagen de la Promoción (URL)</Label>
                      <Input 
                        id="promo-image" 
                        placeholder="https://..." 
                        value={promotionForm.image}
                        onChange={(e) => setPromotionForm({...promotionForm, image: e.target.value})}
                      />
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={isSubmitting} className="w-full">
                        {isSubmitting ? "Publicando..." : "Lanzar Promoción"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {mallPromotions.length === 0 ? (
                  <div className="col-span-full py-12 text-center bg-muted/20 rounded-3xl border border-dashed border-muted-foreground/20">
                    <Gift className="h-12 w-12 text-muted-foreground opacity-20 mx-auto mb-4" />
                    <p className="font-bold">No tienes promociones activas</p>
                    <p className="text-sm text-muted-foreground">Fideliza a tus clientes ofreciéndoles algo especial.</p>
                  </div>
                ) : (
                  mallPromotions.map(promo => (
                    <Card key={promo.id} className="overflow-hidden border-primary/10 hover:shadow-lg transition-all">
                      <div className="h-32 bg-slate-100 italic relative">
                        {promo.image ? (
                          <img src={promo.image} alt={promo.title} className="h-full w-full object-cover" referrerPolicy="no-referrer" />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-muted-foreground/30">
                            <Zap className="h-10 w-10" />
                          </div>
                        )}
                        <Badge className="absolute top-2 right-2 bg-primary/90">
                          {promo.discountCode ? 'Cupón' : 'Beneficio'}
                        </Badge>
                      </div>
                      <CardHeader className="p-4">
                        <CardTitle className="text-base font-bold line-clamp-1">{promo.title}</CardTitle>
                        <CardDescription className="text-xs line-clamp-2">{promo.description}</CardDescription>
                      </CardHeader>
                      <CardFooter className="p-4 pt-0 flex justify-between items-center">
                        <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {new Date(promo.expiryDate).toLocaleDateString()}
                        </div>
                        <Button variant="ghost" size="sm" className="h-8 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" onClick={async () => {
                          if (window.confirm("¿Eliminar esta promoción?")) {
                            await deleteDoc(doc(firestoreDb, "mall_promotions", promo.id));
                            fetchPromotions(selectedShop!.id);
                          }
                        }}>
                          Eliminar
                        </Button>
                      </CardFooter>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="courses">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-primary" />
                  Mis Cursos Online
                </CardTitle>
                <CardDescription>Crea y gestiona tus programas educativos digitales.</CardDescription>
              </div>
              <Button onClick={() => {
                const plan = profile?.subscription?.tier || 'free';
                const limit = PRICING_PLANS.find(p => p.id === plan)?.limits?.maxCourses || 1;
                if (courses.length >= limit) {
                  toast.error(`Has alcanzado el límite de ${limit} cursos de tu plan ${plan.toUpperCase()}`);
                  return;
                }
                toast.info("Funcionalidad de creación de cursos en desarrollo");
              }}>
                <Plus className="h-4 w-4 mr-2" /> Crear Curso
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.length === 0 ? (
                  <div className="col-span-full p-12 border border-dashed rounded-3xl text-center flex flex-col items-center bg-slate-50">
                    <GraduationCap className="h-12 w-12 text-muted-foreground opacity-20 mb-4" />
                    <p className="font-bold">Aún no tienes cursos</p>
                    <p className="text-sm text-muted-foreground mb-6">Comparte tu conocimiento y genera ingresos pasivos.</p>
                    <Button variant="outline">Empezar a enseñar</Button>
                  </div>
                ) : (
                  courses.map(course => (
                    <div key={course.id} className="group border rounded-3xl overflow-hidden hover:shadow-xl transition-all flex flex-col bg-white">
                      <div className="aspect-video bg-slate-100 relative">
                        <img 
                          src={course.image || `https://picsum.photos/seed/${course.id}/800/450`}
                          alt={course.title}
                          className="h-full w-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                        <div className="absolute top-4 right-4 flex gap-2">
                          <Badge className="bg-white/90 backdrop-blur-sm text-primary shadow-sm">
                            ${course.price}
                          </Badge>
                          <Badge className={course.published ? "bg-green-600" : "bg-yellow-500"}>
                            {course.published ? "Publicado" : "Borrador"}
                          </Badge>
                        </div>
                      </div>
                      <div className="p-5 flex-1 flex flex-col">
                        <h4 className="font-bold text-lg mb-2 leading-tight group-hover:text-primary transition-colors">{course.title}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-4">{course.description}</p>
                        
                        <div className="grid grid-cols-2 gap-4 mb-4 mt-auto">
                          <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-xl">
                            <Layers className="h-3 w-3 text-primary" />
                            <div className="flex flex-col">
                              <span className="text-[10px] font-bold leading-none">{course.modules.length}</span>
                              <span className="text-[8px] text-muted-foreground leading-none">Módulos</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-xl">
                            <Users className="h-3 w-3 text-primary" />
                            <div className="flex flex-col">
                              <span className="text-[10px] font-bold leading-none">{course.studentsCount}</span>
                              <span className="text-[8px] text-muted-foreground leading-none">Alumnos</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-[10px] bg-primary/10 text-primary font-bold">{course.instructorName[0]}</AvatarFallback>
                            </Avatar>
                            <span className="text-[10px] font-medium">{course.instructorName}</span>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-primary/10">
                              <Settings className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full hover:bg-primary/10 text-primary">
                              <PlayCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="blog">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Newspaper className="h-5 w-5 text-primary" />
                  Blog de la Tienda
                </CardTitle>
                <CardDescription>Publica contenido de valor para mejorar tu SEO y fidelizar clientes.</CardDescription>
              </div>
              <Button>
                <Plus className="h-4 w-4 mr-2" /> Nueva Entrada
              </Button>
            </CardHeader>
            <CardContent>
              {blogPosts.length === 0 ? (
                <div className="p-12 border border-dashed rounded-3xl text-center flex flex-col items-center">
                  <FileText className="h-12 w-12 text-muted-foreground opacity-20 mb-4" />
                  <p className="font-bold">Tu blog está vacío</p>
                  <p className="text-sm text-muted-foreground mb-6">El marketing de contenidos es clave para atraer tráfico orgánico.</p>
                  <Button variant="outline">Crear mi primer post</Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {blogPosts.map(post => (
                    <div key={post.id} className="group border rounded-2xl overflow-hidden hover:shadow-lg transition-all flex flex-col h-full bg-white">
                      <div className="aspect-video bg-slate-100 relative">
                        <img 
                          src={post.image || `https://picsum.photos/seed/${post.id}/800/600`}
                          alt={post.title}
                          className="h-full w-full object-cover transition-transform group-hover:scale-105"
                          referrerPolicy="no-referrer"
                        />
                        <div className="absolute top-2 right-2">
                          <Badge className={post.published ? "bg-green-600" : "bg-yellow-500"}>
                            {post.published ? "Publicado" : "Borrador"}
                          </Badge>
                        </div>
                      </div>
                      <div className="p-4 flex-1 flex flex-col">
                        <p className="text-[10px] text-muted-foreground font-bold uppercase mb-1 tracking-wider">
                          {new Date(post.createdAt).toLocaleDateString("es-ES", { day: "numeric", month: "long" })}
                        </p>
                        <h4 className="font-bold text-lg mb-2 leading-tight group-hover:text-primary transition-colors">{post.title}</h4>
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-4">{post.content}</p>
                        <div className="mt-auto flex items-center justify-between pt-4 border-t border-slate-50">
                          <div className="flex items-center gap-2">
                            <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary italic">
                              {post.authorName[0]}
                            </div>
                            <span className="text-[10px] font-medium">{post.authorName}</span>
                          </div>
                          <Button variant="ghost" size="sm" className="h-8 group-hover:bg-primary/5">
                            Editar
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="affiliates">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Share2 className="h-5 w-5 text-primary" />
                Programa de Afiliados
              </CardTitle>
              <CardDescription>Gana comisiones recomendando EmpreMarket a otros emprendedores.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 border rounded-2xl bg-slate-50">
                  <p className="text-xs font-bold text-muted-foreground uppercase mb-1">Clicks</p>
                  <p className="text-2xl font-black">{affiliateStats.clicks}</p>
                </div>
                <div className="p-4 border rounded-2xl bg-slate-50">
                  <p className="text-xs font-bold text-muted-foreground uppercase mb-1">Referidos</p>
                  <p className="text-2xl font-black">{affiliateStats.referrals}</p>
                </div>
                <div className="p-4 border rounded-2xl bg-green-50 border-green-100">
                  <p className="text-xs font-bold text-green-700 uppercase mb-1">Ganado</p>
                  <p className="text-2xl font-black text-green-800">${affiliateStats.totalEarnings}</p>
                </div>
                <div className="p-4 border rounded-2xl bg-blue-50 border-blue-100">
                  <p className="text-xs font-bold text-blue-700 uppercase mb-1">Pendiente</p>
                  <p className="text-2xl font-black text-blue-800">${affiliateStats.pendingBalance}</p>
                </div>
              </div>

              <div className="p-6 border rounded-2xl bg-primary/5 border-primary/20">
                <h4 className="font-bold mb-2 flex items-center gap-2">
                  <LinkIcon className="h-4 w-4" /> Tu Enlace de Afiliado
                </h4>
                <div className="flex gap-2">
                  <Input 
                    readOnly 
                    value={`${window.location.origin}/?ref=${user?.uid.slice(0, 8)}`} 
                    className="bg-white font-mono text-sm"
                  />
                  <Button onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/?ref=${user?.uid.slice(0, 8)}`);
                    toast.success("Enlace copiado al portapapeles");
                  }}>Copiar</Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2 italic">
                  Recibe un 30% de comisión recurrente por cada usuario que se suscriba con tu enlace.
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="font-bold">Recursos de Promoción</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-xl flex items-center gap-4 hover:bg-slate-50 cursor-pointer">
                    <Download className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-bold">Kit de Marca</p>
                      <p className="text-[10px] text-muted-foreground">Logos, banners y colores oficiales.</p>
                    </div>
                  </div>
                  <div className="p-4 border rounded-xl flex items-center gap-4 hover:bg-slate-50 cursor-pointer">
                    <Send className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-bold">Plantillas de Email</p>
                      <p className="text-[10px] text-muted-foreground">Copia y pega para tus campañas.</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="certifications">
          <Card className="border-none shadow-none bg-transparent">
            <CardHeader className="px-0">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-3xl font-black flex items-center gap-2">
                    <Award className="h-8 w-8 text-primary" />
                    Centro de Certificación
                  </CardTitle>
                  <CardDescription className="text-lg text-slate-600">Domina EmpreMarket y conviértete en un experto certificado.</CardDescription>
                </div>
                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none px-4 py-2 text-sm font-bold w-fit">
                  Nivel: Aspirante a Experto
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="px-0 space-y-12">
              {/* Hero Banner */}
              <div className="relative overflow-hidden rounded-[40px] bg-slate-900 p-8 md:p-16 text-white border border-white/5 shadow-2xl">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/20 blur-[120px] rounded-full translate-x-1/2 -translate-y-1/2" />
                <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                  <div className="space-y-6">
                    <Badge className="bg-white/10 text-white border-none hover:bg-white/20 px-3 py-1">Entrenamiento Oficial</Badge>
                    <h2 className="text-4xl md:text-5xl font-black leading-tight">Certificación de Experto en la Plataforma</h2>
                    <p className="text-slate-400 text-lg leading-relaxed">
                      Este programa integral gratuito te llevará de principiante a profesional. Aprende a gestionar contactos, automatizar tus ventas y crear embudos de alta conversión que triplicarán tus ingresos.
                    </p>
                    <div className="flex flex-wrap gap-4 pt-4">
                      <Button className="bg-primary hover:bg-primary/90 text-white rounded-2xl px-10 h-16 text-lg font-bold shadow-xl shadow-primary/20 transition-all border-none">
                        Comenzar Certificación
                      </Button>
                      <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-2xl px-10 h-16 text-lg font-bold transition-all">
                        Ver Temario
                      </Button>
                    </div>
                  </div>
                  <div className="hidden md:flex justify-center relative">
                    <div className="w-64 h-64 bg-primary/10 rounded-[60px] flex items-center justify-center border border-primary/20 rotate-12 absolute -z-10 blur-xl translate-x-8" />
                    <div className="w-64 h-64 bg-slate-800 rounded-[60px] flex items-center justify-center border border-white/10 shadow-2xl relative overflow-hidden group">
                      <Award className="h-32 w-32 text-primary drop-shadow-2xl animate-pulse" />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Plan de Estudios */}
              <div className="space-y-8">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                  <div>
                    <h3 className="text-2xl font-black">Plan de Estudios Oficial</h3>
                    <p className="text-slate-500">22 lecciones divididas en 6 bloques esenciales de aprendizaje.</p>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-black text-primary">0% Completado</span>
                      <span className="text-xs text-slate-400 font-medium">(0/22 temas)</span>
                    </div>
                    <div className="h-2 w-48 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-primary w-0 rounded-full" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { id: 1, title: "Bloque 1: Configuración de Cuenta y Perfil", lessons: 4, icon: UserCircle, desc: "Primeos pasos y optimización de tu identidad digital." },
                    { id: 2, title: "Bloque 2: Gestión Maestra de Catálogos", lessons: 5, icon: Store, desc: "Cómo publicar productos y servicios de forma irresistible." },
                    { id: 3, title: "Bloque 3: Estrategias de Embudos", lessons: 3, icon: Filter, desc: "Arquitectura de ventas paso a paso para maximizar conversiones." },
                    { id: 4, title: "Bloque 4: Automatización de Flujos", lessons: 4, icon: Workflow, desc: "Haz que tu negocio trabaje por ti mientras duermes." },
                    { id: 5, title: "Bloque 5: Marketing de Afiliados", lessons: 3, icon: Share2, desc: "Escala tus ingresos creando tu propia red de vendedores." },
                    { id: 6, title: "Bloque 6: Finanzas y Procesadores", lessons: 3, icon: FileText, desc: "Cobros, facturas y gestión de pasarelas de pago." },
                  ].map((module) => (
                    <div key={module.id} className="p-6 border rounded-[32px] bg-white hover:shadow-2xl transition-all duration-300 group cursor-pointer hover:border-primary/20 flex gap-5 items-center">
                      <div className="h-16 w-16 shrink-0 rounded-2xl bg-slate-50 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                        <module.icon className="h-8 w-8 text-slate-300 group-hover:text-primary transition-colors" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-lg group-hover:text-primary transition-colors leading-tight mb-1">{module.title}</h4>
                        <p className="text-xs text-slate-400 mb-2 line-clamp-1">{module.desc}</p>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="text-[8px] h-4 px-1.5 uppercase font-black tracking-widest text-slate-400 bg-transparent group-hover:border-primary/20 group-hover:text-primary transition-all">{module.lessons} Lecciones</Badge>
                          <div className="flex items-center gap-1 text-[8px] font-black text-slate-300 group-hover:text-primary/50 transition-colors">
                            <Clock className="h-2 w-2" /> 15 min aprox.
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="h-12 w-12 rounded-full group-hover:bg-primary/10 transition-all shrink-0">
                        <PlayCircle className="h-6 w-6 text-primary" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Logros */}
              <div className="space-y-6 pt-12 border-t">
                <h3 className="text-2xl font-black flex items-center gap-3">
                  <span className="bg-primary/10 text-primary h-8 w-8 rounded-full flex items-center justify-center text-sm">{certifications.length}</span>
                  Mis Logros y Medallas
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                  {certifications.map(cert => (
                    <div key={cert.id} className="p-6 border rounded-[32px] bg-white flex flex-col items-center text-center hover:shadow-xl transition-all duration-500 border-primary/5 group">
                      <div className="relative mb-3">
                        <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                        <img src={cert.badgeUrl} alt={cert.name} className="relative h-16 w-16 drop-shadow-lg" referrerPolicy="no-referrer" />
                      </div>
                      <p className="text-[10px] font-black leading-tight uppercase tracking-tight">{cert.name}</p>
                      <p className="text-[8px] text-slate-400 mt-1 uppercase font-bold tracking-widest">{cert.level}</p>
                    </div>
                  ))}
                  <div className="p-6 border border-dashed rounded-[32px] flex flex-col items-center justify-center bg-slate-50/50 opacity-30 grayscale cursor-help group hover:opacity-50 transition-all">
                    <div className="h-16 w-16 bg-slate-200 rounded-full flex items-center justify-center mb-3">
                      <Award className="h-8 w-8 text-slate-400" />
                    </div>
                    <p className="text-[10px] font-black">Experto Senior</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </div>
    </Tabs>

      {/* Review Modal for Buyers */}
      <Dialog open={isReviewModalOpen} onOpenChange={setIsReviewModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Califica tu experiencia</DialogTitle>
            <DialogDescription>
              Cuéntanos cómo te fue con tu pedido #{selectedOrderForReview?.id.slice(-6).toUpperCase()}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitReview} className="space-y-4 py-4">
            {selectedOrderForReview && selectedOrderForReview.items.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="review-target">¿Qué quieres calificar?</Label>
                <select 
                  id="review-target"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={newReview.targetId}
                  onChange={(e) => setNewReview({ ...newReview, targetId: e.target.value })}
                >
                  <option value={selectedOrderForReview.shopId}>La Tienda (General)</option>
                  {selectedOrderForReview.items.map((item) => (
                    <option key={item.productId} value={item.productId}>
                      Producto: {item.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Calificación</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setNewReview({ ...newReview, rating: star })}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`h-8 w-8 ${
                        star <= newReview.rating
                          ? "text-yellow-500 fill-yellow-500"
                          : "text-muted"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="review-comment">Tu comentario</Label>
              <Textarea
                id="review-comment"
                placeholder="¿Qué te pareció el producto y la atención?"
                value={newReview.comment}
                onChange={(e) =>
                  setNewReview({ ...newReview, comment: e.target.value })
                }
                required
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? "Enviando..." : "Publicar Reseña"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Review Response Modal */}
      <Dialog open={isReviewResponseModalOpen} onOpenChange={setIsReviewResponseModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Responder a Reseña</DialogTitle>
            <DialogDescription>Tu respuesta será visible para todos los usuarios.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRespondReview} className="space-y-4 py-4">
            <div className="bg-muted p-3 rounded-md text-sm italic">
              "{selectedReview?.comment}"
            </div>
            <div className="grid gap-2">
              <Label htmlFor="response">Tu Respuesta</Label>
              <Textarea 
                id="response" 
                placeholder="Escribe tu respuesta aquí..." 
                value={reviewResponse}
                onChange={(e) => setReviewResponse(e.target.value)}
                required 
                rows={5}
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Enviando..." : "Enviar Respuesta"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Shop Modal */}
      <Dialog open={isEditShopModalOpen} onOpenChange={setIsEditShopModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Perfil de Tienda</DialogTitle>
            <DialogDescription>Actualiza la información pública de tu negocio.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateShop} className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="shop-name">Nombre de la Tienda</Label>
              <Input 
                id="shop-name" 
                value={shopForm.name}
                onChange={(e) => setShopForm({...shopForm, name: e.target.value})}
                required 
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="shop-description">Descripción</Label>
              <Textarea 
                id="shop-description" 
                value={shopForm.description}
                onChange={(e) => setShopForm({...shopForm, description: e.target.value})}
                required 
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="shop-whatsapp">WhatsApp (con código de país)</Label>
                <Input 
                  id="shop-whatsapp" 
                  placeholder="Ej: 5491122334455"
                  value={shopForm.whatsapp}
                  onChange={(e) => setShopForm({...shopForm, whatsapp: e.target.value})}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="shop-email">Email de Contacto</Label>
                <Input 
                  id="shop-email" 
                  type="email"
                  placeholder="Ej: contacto@tienda.com"
                  value={shopForm.email}
                  onChange={(e) => setShopForm({...shopForm, email: e.target.value})}
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="shop-website">Sitio Web</Label>
              <Input 
                id="shop-website" 
                placeholder="Ej: https://tienda.com"
                value={shopForm.website}
                onChange={(e) => setShopForm({...shopForm, website: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="shop-city">Ciudad</Label>
                <Input 
                  id="shop-city" 
                  value={shopForm.city}
                  onChange={(e) => setShopForm({...shopForm, city: e.target.value})}
                  required 
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="shop-country">País</Label>
                <select 
                  id="shop-country" 
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={shopForm.country}
                  onChange={(e) => setShopForm({...shopForm, country: e.target.value})}
                  required 
                >
                  <option value="" disabled>Selecciona un país</option>
                  {COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="shop-instagram">Instagram (usuario)</Label>
                <Input 
                  id="shop-instagram" 
                  placeholder="@usuario"
                  value={shopForm.instagram}
                  onChange={(e) => setShopForm({...shopForm, instagram: e.target.value})}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="shop-facebook">Facebook (URL o usuario)</Label>
                <Input 
                  id="shop-facebook" 
                  value={shopForm.facebook}
                  onChange={(e) => setShopForm({...shopForm, facebook: e.target.value})}
                />
              </div>
            </div>

            <div className="p-4 border border-primary/20 rounded-xl bg-primary/5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Award className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-bold text-sm">Afiliación al Mall Club</h4>
                  <p className="text-[10px] text-muted-foreground">Únete al club para que los socios ganen puntos al comprarte.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  id="shop-club-edit"
                  type="checkbox" 
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  checked={shopForm.isClubAffiliated}
                  onChange={(e) => setShopForm({...shopForm, isClubAffiliated: e.target.checked})}
                />
                <Label htmlFor="shop-club-edit" className="text-xs font-medium">Activo</Label>
              </div>
            </div>

            {!(profile?.subscription?.tier === 'webinar' || profile?.subscription?.tier === 'unlimited') && (
              <div className="p-4 border border-dashed rounded-xl bg-muted/50 text-center py-10">
                <ShieldCheck className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <h3 className="text-lg font-bold">Funciones Premium Bloqueadas</h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-xs mx-auto">
                  Necesitas el plan <span className="font-bold text-primary">Webinar</span> o superior para usar dominios personalizados y galerías avanzadas.
                </p>
                <Button onClick={() => setActiveTab("subscription")}>Ver Planes</Button>
              </div>
            )}
            
            {(profile?.subscription?.tier === 'webinar' || profile?.subscription?.tier === 'unlimited') && (
              <div className="p-4 border border-primary/20 bg-primary/5 rounded-xl space-y-4">
                <div className="flex items-center gap-2 text-primary font-bold">
                  <Zap className="h-4 w-4 fill-primary" />
                  Funciones Premium
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="shop-domain">Dominio Personalizado</Label>
                  <Input 
                    id="shop-domain" 
                    placeholder="Ej: www.mitienda.com"
                    value={shopForm.customDomain}
                    onChange={(e) => setShopForm({...shopForm, customDomain: e.target.value})}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Ingresa el dominio que deseas vincular. Nuestro equipo se contactará para la delegación de DNS.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Ubicación Física (Google Maps)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input 
                      placeholder="Latitud"
                      type="number"
                      step="any"
                      value={shopForm.physicalLocation.lat}
                      onChange={(e) => setShopForm({
                        ...shopForm, 
                        physicalLocation: { ...shopForm.physicalLocation, lat: parseFloat(e.target.value) }
                      })}
                    />
                    <Input 
                      placeholder="Longitud"
                      type="number"
                      step="any"
                      value={shopForm.physicalLocation.lng}
                      onChange={(e) => setShopForm({
                        ...shopForm, 
                        physicalLocation: { ...shopForm.physicalLocation, lng: parseFloat(e.target.value) }
                      })}
                    />
                  </div>
                  <Input 
                    placeholder="Dirección completa para el mapa"
                    value={shopForm.physicalLocation.address}
                    onChange={(e) => setShopForm({
                      ...shopForm, 
                      physicalLocation: { ...shopForm.physicalLocation, address: e.target.value }
                    })}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="shop-premium-photos">Galería Premium (URLs separadas por coma)</Label>
                  <Textarea 
                    id="shop-premium-photos" 
                    placeholder="https://imagen1.jpg, https://imagen2.jpg..."
                    value={shopForm.premiumPhotos}
                    onChange={(e) => setShopForm({...shopForm, premiumPhotos: e.target.value})}
                    rows={3}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Estas fotos aparecerán en una galería especial en tu vitrina.
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="shop-type">Tipo de Tienda</Label>
                <select 
                  id="shop-type"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={shopForm.type}
                  onChange={(e) => setShopForm({...shopForm, type: e.target.value as any})}
                >
                  <option value="products">Solo Productos</option>
                  <option value="services">Solo Servicios</option>
                  <option value="both">Ambos</option>
                </select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="shop-zone">Zona del Mall</Label>
                <select 
                  id="shop-zone"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={shopForm.mallZone}
                  onChange={(e) => setShopForm({...shopForm, mallZone: e.target.value as any})}
                >
                  {MALL_ZONES.map(zone => (
                    <option key={zone.id} value={zone.id}>{zone.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Categorías (separadas por coma)</Label>
                <Input 
                  value={shopForm.categories.join(", ")}
                  onChange={(e) => setShopForm({...shopForm, categories: e.target.value.split(",").map(c => c.trim()).filter(c => c !== "")})}
                  placeholder="Moda, Hogar, Arte..."
                />
              </div>
            <div className="grid gap-2">
              <Label htmlFor="shop-address">Dirección (opcional)</Label>
              <Input 
                id="shop-address" 
                value={shopForm.address}
                onChange={(e) => setShopForm({...shopForm, address: e.target.value})}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="shop-logo">URL del Logo</Label>
              <Input 
                id="shop-logo" 
                value={shopForm.logoURL}
                onChange={(e) => setShopForm({...shopForm, logoURL: e.target.value})}
                placeholder="https://..."
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="shop-banner">URL del Banner</Label>
              <Input 
                id="shop-banner" 
                value={shopForm.bannerURL}
                onChange={(e) => setShopForm({...shopForm, bannerURL: e.target.value})}
                placeholder="https://..."
              />
            </div>
              <div className="flex items-center gap-2 p-4 bg-primary/5 border border-primary/10 rounded-xl">
                <input 
                  type="checkbox" 
                  id="isIntegral" 
                  checked={shopForm.isIntegral}
                  onChange={(e) => setShopForm({...shopForm, isIntegral: e.target.checked})}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <div>
                  <Label htmlFor="isIntegral" className="text-sm font-bold cursor-pointer block">
                    Habilitar Agendamiento de Citas
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Permite a tus clientes solicitar reuniones virtuales en tu sala de video interna de EmpreMarket.
                  </p>
                </div>
              </div>

              {shopForm.isIntegral && (
                <div className="space-y-4 border rounded-xl p-4 bg-muted/20">
                  <h4 className="text-sm font-bold flex items-center gap-2">
                    <Clock className="h-4 w-4" /> Configurar Horarios de Atención
                  </h4>
                  <div className="space-y-3">
                    {shopForm.businessHours.map((bh, idx) => (
                      <div key={bh.day} className="flex items-center justify-between gap-4 p-2 bg-background rounded-lg border text-xs">
                        <div className="flex items-center gap-2 w-24">
                          <input 
                            type="checkbox" 
                            checked={bh.active}
                            onChange={(e) => {
                              const newHours = [...shopForm.businessHours];
                              newHours[idx].active = e.target.checked;
                              setShopForm({...shopForm, businessHours: newHours});
                            }}
                            className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                          <span className="font-medium">{DAYS[bh.day]}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 flex-1 justify-end">
                          <Input 
                            type="time" 
                            className="h-7 w-24 text-[10px] p-1"
                            value={bh.open}
                            disabled={!bh.active}
                            onChange={(e) => {
                              const newHours = [...shopForm.businessHours];
                              newHours[idx].open = e.target.value;
                              setShopForm({...shopForm, businessHours: newHours});
                            }}
                          />
                          <span>a</span>
                          <Input 
                            type="time" 
                            className="h-7 w-24 text-[10px] p-1"
                            value={bh.close}
                            disabled={!bh.active}
                            onChange={(e) => {
                              const newHours = [...shopForm.businessHours];
                              newHours[idx].close = e.target.value;
                              setShopForm({...shopForm, businessHours: newHours});
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground italic">
                    Estos horarios limitarán cuándo los clientes pueden solicitar citas en tu tienda.
                  </p>
                </div>
              )}
            <DialogFooter>
              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? "Guardando..." : "Guardar Cambios de Tienda"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Product Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Editar Producto</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateProduct} className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Nombre</Label>
              <Input 
                id="edit-name" 
                value={productForm.name}
                onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                required 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-price">Precio ($)</Label>
                <Input 
                  id="edit-price" 
                  type="number" 
                  value={productForm.price}
                  onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                  required={!productForm.priceToAgree}
                  disabled={productForm.priceToAgree}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-stock">Stock</Label>
                <Input 
                  id="edit-stock" 
                  type="number" 
                  value={productForm.stock}
                  onChange={(e) => setProductForm({...productForm, stock: e.target.value})}
                  required 
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="edit-priceToAgree" 
                checked={productForm.priceToAgree}
                onChange={(e) => setProductForm({...productForm, priceToAgree: e.target.checked, price: e.target.checked ? "0" : productForm.price})}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <Label htmlFor="edit-priceToAgree" className="text-sm font-medium cursor-pointer">
                Precio a convenir (no mostrar precio fijo)
              </Label>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-category">Categoría</Label>
              <select 
                id="edit-category"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={productForm.category}
                onChange={(e) => setProductForm({...productForm, category: e.target.value})}
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-image">URL de Imagen</Label>
              <Input 
                id="edit-image" 
                value={productForm.image}
                onChange={(e) => setProductForm({...productForm, image: e.target.value})}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-tags">Etiquetas (separadas por coma)</Label>
              <Input 
                id="edit-tags" 
                value={productForm.tags}
                onChange={(e) => setProductForm({...productForm, tags: e.target.value})}
                placeholder="Ej: orgánico, artesanal, regalo"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-description">Descripción</Label>
              <Textarea 
                id="edit-description" 
                value={productForm.description}
                onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                required 
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
